import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { getPersonDetails, getCaptureLog, regenerateBio, getRecordTake, generateRecordTake } from "../api";
import { Lightbox } from "../components/Lightbox";
import { PetitionerDetailsModal } from "../components/PetitionerDetailsModal";
import { ProfileEditModal } from "../components/ProfileEditModal";
import { PageHelpHandle } from "../components/PageHelpHandle";
import { recordPersonVisit } from "../components/CommandPalette";
import { IdentifiersModal, type IdentifierField } from "../components/IdentifiersModal";
import { LetterModal } from "../components/LetterModal";
import { runWithToast } from "../lib/downloadToast";
import { ExternalSourcesPanel } from "../components/ExternalSourcesPanel";
import { BackgroundChecksStatus } from "../components/BackgroundChecksStatus";
// VisualizeView pulls d3 + d3-sankey + flow chart code (~150KB+ minified).
// Only shown when the user toggles to the Visualize tab — most staff
// never click it, so we lazy-load to keep the initial PersonPage bundle
// tight. Suspense fallback at the call site.
const VisualizeView = lazy(() =>
  import("../components/VisualizeView").then((m) => ({ default: m.VisualizeView })),
);
import { FloatingDock } from "../components/FloatingDock";
import { ResumeBuilderModal } from "../components/ResumeBuilderModal";
import { groupFields, isOpenStatus, recordStatus } from "../lib/fieldGroups";
import { recordBalance, buildCollectionMap, recordHasActiveWarrant, type RecordCollectionEntry } from "../lib/openItems";
import { EXTERNAL_SOURCE_ORDER, EXTERNAL_SOURCES, FIELD_LABELS, FIELD_LABELS_SHORT, missingRequiredFields, sourceLabel, type RequiredField } from "../lib/externalSources";
import { classifyOffense, categorizeOffense, SEVERITY_LABEL, SEVERITY_TEXT, type OffenseCategory, type Severity } from "../lib/severity";
// Offense-category watermarks — gpt-image-1-generated white-on-transparent
// PNGs in vintage etched/engraved style. Vite resolves these to hashed
// URLs at build time. Imported once at module scope so the same URLs
// are reused across all OffenseCard instances (no per-render lookup).
import vehicleIcon from "../assets/offense-icons/vehicle.png";
import drugIcon from "../assets/offense-icons/drug.png";
import weaponIcon from "../assets/offense-icons/weapon.png";
import assaultIcon from "../assets/offense-icons/assault.png";
import propertyIcon from "../assets/offense-icons/property.png";
import publicOrderIcon from "../assets/offense-icons/public-order.png";
import fraudIcon from "../assets/offense-icons/fraud.png";
import otherIcon from "../assets/offense-icons/other.png";
import { havocTier, parseCommentary, HAVOC_SCORE_EXPLAINER } from "../lib/commentary";
import { renderInlineMarkdown } from "../lib/inlineMarkdown";
import { pronounFor } from "../lib/pronouns";
import { playForScore } from "../lib/voice";
import { toast } from "../components/Toaster";
import {
  ExpungementSection,
  ResourcesSection,
} from "../components/PersonAnalysis";
import { OpenItemsSummary } from "../components/OpenItemsSummary";
import { LicenseSuspendedBanner } from "../components/LicenseSuspendedBanner";
import { ActiveWarrantsBanner } from "../components/ActiveWarrantsBanner";
import { togglePinned, isPinned, PINNED_KEY } from "../lib/pinnedJobs";
import { NewsMentionsPanel } from "../components/NewsMentionsPanel";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { useExternalLookups } from "../lib/useExternalLookups";
import type { ExternalLookup, ExternalSource } from "../components/ExternalSourcesPanel";
import { NotesSection } from "../components/NotesSection";
import type { CaptureAttempt, JobRow, PersonDetails, RecordData, RecordRow, ScreenshotRow, ServerEvent } from "../types";

// ─────────────────────────────────────────────────────────────────────────
// Image fallback — swap broken screenshot thumbnails for a soft placeholder
// instead of the browser's broken-image icon.
// ─────────────────────────────────────────────────────────────────────────
const TRANSPARENT_PX =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'/>";
function handleShotError(e: React.SyntheticEvent<HTMLImageElement>): void {
  const img = e.currentTarget;
  if (img.dataset.broken === "1") return;
  img.dataset.broken = "1";
  img.src = TRANSPARENT_PX;
  img.classList.add("opacity-30");
  img.style.background = "var(--color-bg-card-hi)";
}

type ViewMode = "cards" | "table" | "visualize" | "log" | "screenshots";

function isRecordData(x: unknown): x is RecordData {
  return (
    !!x &&
    typeof x === "object" &&
    "summary" in (x as Record<string, unknown>) &&
    typeof (x as { summary: unknown }).summary === "object"
  );
}

function pick(s: Record<string, string | string[]>, key: string): string {
  const v = s[key];
  if (!v) return "";
  return Array.isArray(v) ? v[v.length - 1] ?? "" : v;
}

// Source classifier — buckets a record into the court system that produced
// it. If the Court field already names a court system explicitly, trust
// that. Otherwise fall back to case-number prefix heuristics.
//
// 2026-05-13: prefix-based Superior detection requires a DASH after the
// I/S letter (matching genuine indictment numbers like S-2024-1234). The
// old `^S[-\s]` pattern matched "S " Special Form Complaints — a NORMAL
// Municipal court complaint type — and routed them to Superior. Tracy
// Nixon's audit surfaced this: two Jersey City Municipal records ended
// up tagged Superior because their numbers started "S ".
type SourceBucket = "municipal" | "superior" | "federal" | "other";
function classifyRecordSource(rec: RecordRow): SourceBucket {
  if (!isRecordData(rec.data)) return "other";
  const s = rec.data.summary;
  const court = `${pick(s, "Court")} ${pick(s, "Court Name")}`.toLowerCase();
  if (/municipal/.test(court)) return "municipal";
  if (/superior|county court|indictment/.test(court)) return "superior";
  const prefix = pick(s, "Prefix/Year/Number").toUpperCase();
  const caseNum = (rec.case_number ?? "").toUpperCase();
  if (/^I-|^S-|^IND/.test(prefix) || /^I-|^S-|^IND/.test(caseNum)) return "superior";
  if (/^[A-Z]{2,4}\b/.test(court)) return "municipal";
  if (/^SC\b|^TR\b|^MUN/.test(prefix)) return "municipal";
  return "other";
}

// Origin scraper for an offense card. Drives the source-card filter
// toggle (clicking the PGPA card narrows offense cards to PGPA-sourced
// ones, etc.). Distinct from classifyRecordSource above, which buckets
// by COURT SYSTEM, not by which scraper actually produced the row.
//
// Synthesized PGPA records carry a negative id (see synthesizePgpaRecord
// below). Everything else is a real `records` table row from the MPA
// scrape.
function recordOriginSource(rec: RecordRow): "mpa" | "pgpa" {
  return rec.id < 0 ? "pgpa" : "mpa";
}

// PGPA → RecordRow adapter. Lets us render Superior Court cases as
// offense cards alongside Municipal ones, instead of hiding them in a
// side panel where staff can miss them entirely.
//
// Drops phantom rows (caseNumber === "CASE", or no county) that come
// from the PGPA scraper occasionally picking up a label row. Heals
// character-split charges arrays where the parser fed a string in
// one character at a time. Both are render-layer workarounds for
// scraper bugs we haven't fixed at the source yet.
function isValidPgpaCase(c: unknown): boolean {
  if (!c || typeof c !== "object") return false;
  const cn = (c as { caseNumber?: unknown }).caseNumber;
  if (typeof cn !== "string" || cn.trim() === "" || cn.toUpperCase() === "CASE") return false;
  const county = (c as { county?: unknown }).county;
  if (county === null || county === undefined || county === "") return false;
  return true;
}

// PGPA "action" code → short label suitable for a status pill (1-2
// words). Long-form description lives in the modal subPage. These
// two-character codes appear both as a standalone status field and
// embedded in `c.raw.action` alongside `c.raw.reason` (the long-form
// reason).
function shortPgpaAction(code: string): string {
  const c = code.toUpperCase().trim();
  if (c === "RM") return "Remanded";
  if (c === "GT") return "Guilty (lesser)";
  if (c === "GP" || c === "GU") return "Guilty";
  if (c === "DM") return "Dismissed";
  if (c === "AC") return "Acquitted";
  if (c === "NG") return "Not guilty";
  if (c === "PR") return "PTI";
  return code;
}

// Slightly longer plain-English version, used in the modal where we
// have horizontal room.
function prettyPgpaAction(code: string): string {
  const c = code.toUpperCase().trim();
  if (c === "RM") return "Remanded to Municipal Court";
  if (c === "GT") return "Guilty plea (lesser charge)";
  if (c === "GP") return "Guilty plea";
  if (c === "GU") return "Guilty (trial)";
  if (c === "DM") return "Dismissed";
  if (c === "AC") return "Acquitted";
  if (c === "NG") return "Not guilty";
  if (c === "PR") return "PTI (pre-trial intervention)";
  return code;
}

// Status pill text: short action-code label only. Long-form reason
// lives in the Case Detail subPage. Without trimming we'd render
// "Guilty plea (lesser charge) — GUILTY PLEA LESS IND" on the card
// which overflows.
function prettyPgpaStatus(rawStatus: string, action?: string, _reason?: string): string {
  const a = (action || "").toUpperCase().trim();
  if (a) return shortPgpaAction(a);
  const s = (rawStatus || "").trim();
  if (!s || s.toUpperCase() === "REASON") return "—";
  // Strip the "XX - …" action-code prefix when present; use only the
  // code half for the pill.
  const m = s.match(/^([A-Z]{2})\s*-\s*.+$/);
  if (m) return shortPgpaAction(m[1] ?? "");
  return s.length > 18 ? s.slice(0, 18) + "…" : s;
}

// Format a "MM DD YYYY" PGPA date string as MM/DD/YYYY for display.
// Empty / unrecognized → return as-is so we don't lose data.
function formatPgpaDate(raw: string): string {
  const m = raw.trim().match(/^(\d{1,2})\s+(\d{1,2})\s+(\d{4})$/);
  if (!m) return raw;
  return `${m[1]?.padStart(2, "0")}/${m[2]?.padStart(2, "0")}/${m[3]}`;
}

interface RawPgpaCase {
  caseNumber?: string;
  county?: string | null;
  filedAt?: string | null;
  status?: string;
  defendant?: string;
  charges?: unknown;
  raw?: Record<string, string> | null;
  sentence?: { rawText?: string; fields?: Record<string, string> } | null;
  caseDetail?: {
    rawText?: string;
    fields?: {
      charges?: string[];
      indictmentNo?: string;
      defenseAttorney?: string;
      defenseAttorneyType?: string;
      jailDays?: string;
      sentLength?: string;
      chargingDocument?: string;
      dispositionDate?: string;
      dispositionAction?: string;
      dispositionReason?: string;
      sentenceDate?: string;
      transferTo?: string;
      jailCaseIndicator?: string;
      [k: string]: unknown;
    };
  } | null;
}

// Client-side fallback parser for PGPA case-detail rawText. Used when
// the scrape pre-dates the server-side regex pass (existing rows from
// before the 2026-05-13 update), so staff get the new data without
// waiting for a re-scrape.
function extractPgpaDetailFields(rawText: string): Record<string, string> {
  if (!rawText || rawText.length < 50) return {};
  // Date fields are "MM DD YYYY" with internal spaces. Old pattern stopped
  // at first whitespace and gave "09"; require an exact 4-digit-year end.
  const dateRe = (label: string) =>
    new RegExp(`${label}\\s*:\\s*([0-9]{1,2}\\s+[0-9]{1,2}\\s+[0-9]{4})`, "i");
  // Stop on the next KNOWN label or banner. Explicit whitelist
  // (generic next-capital patterns over-fire on values like
  // "PADILLA JULIAN L"). Mirrors the server-side regex in
  // src/worker/scrapers/pgpa.ts — keep in sync.
  const STOP_LABELS =
    "(?=\\s+(?:DEFENDANT|DEFN|JAIL|SENT|CHARGING|CASE|TRANSFER|DISPOSITION|DISP|SENTENCE|TRACK|PROSECUTOR|RACE|SEX|S\\s+U\\s+M)\\b|\\s+\\*{3,}|$)";
  const grab = (re: RegExp): string => {
    const m = rawText.match(re);
    return m?.[1]?.trim() ?? "";
  };
  const out: Record<string, string> = {};
  const fields = [
    ["defenseAttorney", new RegExp(`DEFN\\s*ATTY\\s*NAME\\s*:\\s*([A-Z][A-Z0-9 .'\\-/]{0,60}?)${STOP_LABELS}`, "i")],
    ["defenseAttorneyType", new RegExp(`DEFN\\s*ATTY\\s*TYPE\\s*:\\s*([A-Z][A-Z0-9 .'\\-/]{0,40}?)${STOP_LABELS}`, "i")],
    ["jailDays", /JAIL\s*DAYS\s*:\s*([0-9]{1,5})/i],
    ["sentLength", /SENT\s*LENGTH\s*:\s*([0-9]{3}Y[0-9]{2}M[0-9]{3})/i],
    ["chargingDocument", new RegExp(`CHARGING\\s*DOCUM\\s*:\\s*([A-Z][A-Z0-9 ]{0,40}?)${STOP_LABELS}`, "i")],
    ["dispositionDate", dateRe("DISPOSITION\\s*DATE")],
    ["dispositionAction", new RegExp(`DISP\\s*ACTION\\s*:\\s*([A-Z][A-Z0-9 ]{0,40}?)${STOP_LABELS}`, "i")],
    ["dispositionReason", new RegExp(`DISP\\s*REASON\\s*:\\s*([A-Z][A-Z0-9 /\\-]{0,80}?)${STOP_LABELS}`, "i")],
    ["sentenceDate", dateRe("SENTENCE\\s*DATE")],
    ["transferTo", new RegExp(`TRANSFER\\s*TO\\s*:\\s*([A-Z][A-Z0-9 ]{1,50}?)${STOP_LABELS}`, "i")],
    ["jailCaseIndicator", /JAIL\s*CASE\s*IND\s*:\s*([YN])/i],
  ] as const;
  for (const [k, re] of fields) {
    const v = grab(re as RegExp);
    if (v) out[k] = v;
  }
  return out;
}

// Convert PGPA sentence-length code "001Y06M000" to "1y 6mo". Empty /
// all-zero gets a clean "—" so the UI doesn't render junk.
function prettyPgpaSentLength(code: string): string {
  const m = code.match(/^(\d{3})Y(\d{2})M(\d{3})$/);
  if (!m) return code;
  const y = parseInt(m[1] ?? "0", 10);
  const mo = parseInt(m[2] ?? "0", 10);
  const d = parseInt(m[3] ?? "0", 10);
  const parts: string[] = [];
  if (y > 0) parts.push(`${y}y`);
  if (mo > 0) parts.push(`${mo}mo`);
  if (d > 0) parts.push(`${d}d`);
  return parts.length > 0 ? parts.join(" ") : "—";
}

function synthesizePgpaRecord(c: RawPgpaCase, lookupId: number, idx: number, jobId: string): RecordRow {
  const raw: Record<string, string> = (c.raw && typeof c.raw === "object") ? c.raw : {};
  const indictAcc = (raw.indictAcc || "").trim();
  const sbi = (raw.sbi || "").trim();
  const defendant = (raw.defnName || c.defendant || raw.birthName || "").trim();
  const chargDoc = (raw.chargDoc || "").trim().toUpperCase();
  const docType =
    chargDoc === "IN" ? "Indictment" :
    chargDoc === "AC" ? "Accusation" :
    chargDoc === "CO" ? "Complaint" :
    chargDoc === "DP" ? "Disposition" : chargDoc;
  const action = (raw.action || "").trim();
  const reason = (raw.reason || "").trim();
  const filedAtRaw = (raw.initDate || c.filedAt || "").trim();
  const dispDateRaw = (raw.dispDate || "").trim();
  const sentenceDateRaw = (raw.sentenceDate || "").trim();
  const county = (c.county || raw.county || "").trim();
  const courtLabel = county ? `${county} County Superior Court` : "NJ Superior Court";
  const statusPretty = prettyPgpaStatus(c.status ?? "", action, reason);

  // Offense field — short SEMANTIC title that fits the card without
  // wrapping over multiple lines. PGPA's public-access portal doesn't
  // expose per-count statute data (verified 2026-05-13), so the most
  // meaningful headline is "doc type — outcome": "Indictment — Guilty
  // plea (lesser charge)", "Accusation — Dismissed", etc. The case /
  // indictment number lives in the modal's Identity section so the
  // title can stay scannable.
  //
  // detailCharges path is kept for the (hypothetical) future where
  // we recover per-count data — but PGPA charges page is empty so it
  // never runs. Defensive.
  const detailCharges = c.caseDetail?.fields?.charges;
  let offenseDesc: string;
  if (Array.isArray(detailCharges) && detailCharges.length > 0) {
    offenseDesc = detailCharges.slice(0, 2).join("  •  ");
    if (detailCharges.length > 2) {
      offenseDesc += `  •  +${detailCharges.length - 2} more`;
    }
  } else {
    const parts: string[] = [];
    if (docType) parts.push(docType);
    if (action) parts.push(prettyPgpaAction(action));
    offenseDesc = parts.length > 0 ? parts.join(" — ") : "Superior Court matter";
  }

  // Build the summary, dropping empty fields so the detail view doesn't
  // render rows full of blanks. FieldGroups will route each non-empty
  // key into Identity / Case / Court / Charge / Hearing.
  const summary: Record<string, string | string[]> = {};
  if (defendant) summary["Defendant"] = defendant;
  if (sbi) summary["SBI"] = sbi;
  if (c.caseNumber) summary["Case Number"] = c.caseNumber;
  if (indictAcc) summary["Indictment/Accusation No."] = indictAcc;
  summary["Court"] = courtLabel;
  summary["Court Name"] = courtLabel;
  if (statusPretty && statusPretty !== "—") summary["Status"] = statusPretty;
  if (filedAtRaw) summary["Filing Date"] = formatPgpaDate(filedAtRaw);
  if (offenseDesc) summary["Offense Description"] = offenseDesc;
  if (reason && reason.toUpperCase() !== "REASON") summary["Disposition"] = reason;
  if (dispDateRaw) summary["Disposition Date"] = formatPgpaDate(dispDateRaw);
  if (sentenceDateRaw) summary["Sentence Date"] = formatPgpaDate(sentenceDateRaw);
  if (c.caseNumber) summary["Prefix/Year/Number"] = c.caseNumber;

  // Merge server-parsed fields with client-side fallback parsing. Existing
  // scrapes pre-2026-05-13 didn't have the server-side regex pass — we
  // reparse their captured rawText here so staff get the same data
  // without waiting for a re-scrape. New scrapes have everything pre-
  // parsed; the fallback is a no-op for them.
  const serverDetailFields: Record<string, unknown> = c.caseDetail?.fields ?? {};
  const fallback = extractPgpaDetailFields(c.caseDetail?.rawText ?? "");
  const mergedDetail: Record<string, string | string[]> = {};
  for (const [k, v] of Object.entries(serverDetailFields)) {
    if (typeof v === "string" && v.trim()) mergedDetail[k] = v.trim();
    else if (Array.isArray(v) && v.length > 0) mergedDetail[k] = v;
  }
  for (const [k, v] of Object.entries(fallback)) {
    if (!mergedDetail[k] && v) mergedDetail[k] = v;
  }
  // detailCharges already populated above; re-resolve from merged data
  // so the fallback path benefits the card title too.
  const mergedCharges = mergedDetail.charges;
  const finalDetailCharges = Array.isArray(detailCharges) && detailCharges.length > 0
    ? detailCharges
    : (Array.isArray(mergedCharges) ? mergedCharges : undefined);

  // Build subPages so the modal's expanded view shows the full charge
  // list + sentence info + detail summary. Each subPage renders as a
  // labeled details/disclosure block via FullRecordView → FieldGroups.
  const subPages: Array<{ kind: string; fields: Record<string, string | string[]> }> = [];
  if (Array.isArray(finalDetailCharges) && finalDetailCharges.length > 0) {
    const chargeFields: Record<string, string | string[]> = {};
    finalDetailCharges.forEach((line, i) => {
      chargeFields[`Count ${i + 1}`] = line.replace(/^Count\s+\d+\s*[·:]\s*/i, "");
    });
    subPages.push({ kind: "Charges", fields: chargeFields });
  }
  // Case Detail summary block — defense attorney, jail days, sentence
  // length, disposition. Visible on EVERY case-detail page; gives staff
  // the full forensic snapshot without leaving the modal.
  const detailSummary: Record<string, string | string[]> = {};
  const asStr = (k: string): string => (typeof mergedDetail[k] === "string" ? mergedDetail[k] as string : "");
  const da = asStr("defenseAttorney");
  const dat = asStr("defenseAttorneyType");
  if (da) detailSummary["Defense Attorney"] = dat ? `${da} (${dat})` : da;
  const jd = asStr("jailDays");
  if (jd) detailSummary["Jail Days"] = jd;
  const sl = asStr("sentLength");
  if (sl) detailSummary["Sentence Length"] = prettyPgpaSentLength(sl);
  const cd2 = asStr("chargingDocument");
  if (cd2) detailSummary["Charging Document"] = cd2;
  const ddate = asStr("dispositionDate");
  if (ddate) detailSummary["Disposition Date"] = formatPgpaDate(ddate);
  const daction = asStr("dispositionAction");
  if (daction) detailSummary["Disposition Action"] = daction;
  const dreason = asStr("dispositionReason");
  if (dreason) detailSummary["Disposition Reason"] = dreason;
  const sdate = asStr("sentenceDate");
  if (sdate) detailSummary["Sentence Date"] = formatPgpaDate(sdate);
  const xfer = asStr("transferTo");
  if (xfer) detailSummary["Transfer To"] = xfer;
  const jci = asStr("jailCaseIndicator");
  if (jci) detailSummary["Jail Case"] = jci === "Y" ? "Yes" : "No";
  if (Object.keys(detailSummary).length > 0) {
    subPages.push({ kind: "Case Detail", fields: detailSummary });
  }

  const sentenceFields = (c.sentence?.fields ?? {}) as Record<string, string>;
  const hasSentenceFields = Object.values(sentenceFields).some(
    (v) => typeof v === "string" && v.trim() !== "",
  );
  if (hasSentenceFields) {
    const sf: Record<string, string | string[]> = {};
    if (sentenceFields.sentenceDate) sf["Sentence Date"] = formatPgpaDate(sentenceFields.sentenceDate);
    if (sentenceFields.term) sf["Term"] = sentenceFields.term;
    if (sentenceFields.concurrent === "Y") sf["Concurrent"] = "Yes";
    if (sentenceFields.consecutive === "Y") sf["Consecutive"] = "Yes";
    if (sentenceFields.fines) sf["Fines"] = `$${sentenceFields.fines}`;
    if (sentenceFields.credit) sf["Credit for time served"] = sentenceFields.credit;
    if (sentenceFields.specialConditions) sf["Special Conditions"] = sentenceFields.specialConditions;
    if (Object.keys(sf).length > 0) subPages.push({ kind: "Sentence", fields: sf });
  }

  return {
    // Negative id so synthesized rows never collide with real records-table
    // rows (which always have positive autoinc ids). Pulled apart from
    // lookupId so each case in a single PGPA scrape gets a distinct id.
    id: -1_000_000 - lookupId * 1000 - idx,
    job_id: jobId,
    case_number: c.caseNumber ?? "",
    raw_json: JSON.stringify(c),
    data: {
      summary,
      // CRITICAL: OffenseModal's FullRecordView reads data.subPages.length.
      // Omitting this field throws and blacks out the modal. Sub-pages
      // surface the per-count Charges block + Sentence block when the
      // worker drilled in successfully.
      subPages,
    },
  } as unknown as RecordRow;
}

const SOURCE_LABEL: Record<SourceBucket, string> = {
  municipal: "Municipal",
  superior: "Superior",
  federal: "Federal",
  other: "Other",
};

// Strip leading court code prefix ("1608 - PATERSON…" → "Paterson Municipal Court")
function prettyCourt(raw: string): string {
  const cleaned = raw.replace(/^\d+\s*-\s*/, "").trim();
  // Normalize ALL CAPS to Title Case (most NJ municipal court strings are SHOUTED).
  if (cleaned === cleaned.toUpperCase()) {
    return cleaned
      .toLowerCase()
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return cleaned;
}

function shortDate(raw: string): string {
  // Accept "MM/DD/YYYY" or "MM/DD/YYYY HH:MM AM" or already-short.
  const m = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return raw;
  return `${m[1]}/${m[2]}/${m[3]}`;
}

/** Pick the most useful date for display from a record summary.
 *
 * Court-date / filing-date / offense-date are the obvious choices, but
 * ticket-only and payment-only records (e.g. parking, "smoking in parks")
 * often have NONE of those — they instead carry "Starting Date" and
 * "Complete Payment Due Date", or just a year embedded in
 * "Prefix/Year/Number" like "SC 2020 143357". Falling all the way through
 * means we almost never have to render an empty cell.
 *
 * Returns "" only when truly nothing date-like is present.
 */
function pickRecordDate(s: Record<string, string | string[]>): string {
  const tryKey =
    pick(s, "Court Date/Time") ||
    pick(s, "Court Date") ||
    pick(s, "Filing Date") ||
    pick(s, "Offense Date") ||
    pick(s, "Starting Date") ||
    pick(s, "Complete Payment Due Date");
  if (tryKey) return tryKey;
  // Last resort: year encoded in "Prefix/Year/Number" → "SC 2020 143357"
  const pyn = pick(s, "Prefix/Year/Number");
  const yr = pyn.match(/\b(19|20)\d{2}\b/);
  return yr ? yr[0] : "";
}

/** Render a date-ish string for display. If only a year survived the
 *  fallback chain, mark it as such so users know it's approximate. */
function displayDate(raw: string): string {
  if (!raw) return "Date not on file";
  if (/^\d{4}$/.test(raw)) return `${raw} (year only)`;
  return shortDate(raw);
}

// ─────────────────────────────────────────────────────────────────────────
// Cards view — uniform severity-tinted cards, Fraunces serif headline.
// ─────────────────────────────────────────────────────────────────────────

// Per-category watermark URL — gpt-image-1-generated white-on-transparent
// PNGs in vintage etched/engraved style. Each is rendered with low alpha
// inside the OffenseCard so it reads as "lighter shade of whatever color
// the card is" rather than as a solid graphic.
const CATEGORY_WATERMARK_URL: Record<OffenseCategory, string> = {
  vehicle: vehicleIcon,
  drug: drugIcon,
  weapon: weaponIcon,
  assault: assaultIcon,
  property: propertyIcon,
  "public-order": publicOrderIcon,
  fraud: fraudIcon,
  other: otherIcon,
};

// Severity gradient — color-theory journey from cool/calm (minor) to
// hot/urgent (severe), all hues chosen to relate to bordeaux #8C1F2F.
// Dark mode keeps the original drenched palette (minor=slate-blue,
// moderate=teal-forest, serious=terracotta-bronze, severe=bordeaux).
// Light mode mixes each hue with parchment so the tile reads as a
// brand-tinted feature card on the cream page rather than a dark-mode
// chip imported wholesale.
const SEVERITY_GRADIENT_DARK: Record<Severity, string> = {
  minor:    "linear-gradient(155deg, #2E4258 0%, #14202E 100%)",
  moderate: "linear-gradient(155deg, #1F4A4A 0%, #112D2D 100%)",
  serious:  "linear-gradient(155deg, #8B5326 0%, #4D2D12 100%)",
  severe:   "linear-gradient(155deg, #8C1F2F 0%, #5A1320 100%)",
};
const SEVERITY_GRADIENT_LIGHT: Record<Severity, string> = {
  minor:    "linear-gradient(155deg, #D6DCE3 0%, #B9C3D1 100%)",
  moderate: "linear-gradient(155deg, #CDDDD9 0%, #A6BBB6 100%)",
  serious:  "linear-gradient(155deg, #E5CDA8 0%, #C9A57A 100%)",
  severe:   "linear-gradient(155deg, #E6BBC0 0%, #C77F8B 100%)",
};
// Accent hex used for the suggested-resources etc. card-accent stripe.
// Dark mode pulls the brand color; light mode pulls a saturated mid-tone
// rather than the pale gradient end (which would be invisible).
const SEVERITY_ACCENT_DARK: Record<Severity, string> = {
  minor:    "#2E4258",
  moderate: "#1F4A4A",
  serious:  "#8B5326",
  severe:   "#8C1F2F",
};
const SEVERITY_ACCENT_LIGHT: Record<Severity, string> = {
  minor:    "#5B7592",
  moderate: "#3F7570",
  serious:  "#A06B30",
  severe:   "#A8334A",
};
// Tiles use the drenched dark gradient in BOTH themes — the pastel
// light-mode experiment (SEVERITY_GRADIENT_LIGHT below) washed out
// the cards so badly the severity identity vanished. The light maps
// are kept for reference but the helpers always return DARK now.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _SEVERITY_GRADIENT_LIGHT_UNUSED = SEVERITY_GRADIENT_LIGHT;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _SEVERITY_ACCENT_LIGHT_UNUSED = SEVERITY_ACCENT_LIGHT;
const SEVERITY_GRADIENT: Record<Severity, string> = SEVERITY_GRADIENT_DARK;
function severityGradient(s: Severity): string {
  return SEVERITY_GRADIENT_DARK[s];
}
function severityAccent(s: Severity): string {
  return SEVERITY_ACCENT_DARK[s];
}
function severityInk(): string {
  return "#FFFFFF";
}
function severitySubInk(): string {
  return "rgba(255,255,255,0.78)";
}

function OffenseCard({
  rec,
  onOpen,
  collectionEntry,
}: {
  rec: RecordRow;
  onOpen: () => void;
  /** Non-null when this record's balance is part of a multi-record
   *  collection account (the same rolled-up balance shown on N tickets).
   *  When isCollectionGroup=true the per-card balance chip changes to
   *  make clear this is a SHARED balance, not an isolated $X-per-ticket
   *  amount — otherwise paralegals could mentally multiply N × $498 and
   *  scare a client. Computed once at the parent via buildCollectionMap. */
  collectionEntry?: RecordCollectionEntry | null;
}) {
  const data = rec.data;
  if (!isRecordData(data)) {
    return (
      <button onClick={onOpen} className="card text-left h-full">
        <pre className="text-xs">{JSON.stringify(rec.data, null, 2)}</pre>
      </button>
    );
  }
  const rawOffense =
    pick(data.summary, "Offense Description") || pick(data.summary, "Offense") || "—";
  // Prefer the LLM-translated short form (e.g. "Drug possession — Schedule
  // I-IV") when present. Falls back to the raw uppercase court text.
  const offense = rec.human_offense ?? rawOffense;
  const dateRaw = pickRecordDate(data.summary);
  const courtRaw = pick(data.summary, "Court");
  const status = recordStatus(data.summary);
  // Severity classifier expects the raw uppercase text (POSS CDS, SIMPLE
  // ASSAULT, etc.) — not the humanized rephrasing.
  const severity = classifyOffense(rawOffense);
  // Unpaid balance — only shown when the case is OPEN AND there's still
  // money owed (not paid in full, not closed/disposed). Uses the same
  // recordBalance helper that the OpenItemsSummary aggregates from, so
  // the number on the card matches the headline total.
  const balance = recordBalance(rec);
  const showBalance = isOpenStatus(status) && typeof balance === "number" && balance > 0;
  const isRolledUp = collectionEntry?.isCollectionGroup ?? false;
  // Category drives which watermark icon sits behind the card content.
  // Vehicle → steering wheel, weapon → blade, drug → pill, etc.
  const category = categorizeOffense(rawOffense);

  return (
    <button
      type="button"
      id={`record-${rec.id}`}
      onClick={onOpen}
      className="severity-tile group relative flex w-full flex-col items-stretch rounded-2xl border border-transparent text-left transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-black/40 scroll-mt-24"
      style={{
        background: severityGradient(severity),
        color: severityInk(),
        minHeight: 280,
        padding: 0,
        // Clip the watermark icon to the rounded corners.
        overflow: "hidden",
      }}
    >
      {/* Category watermark — gpt-image-1-generated white-on-transparent
          etched illustration. Dead-centered, 105% width so the edges
          bleed off the card (clipped by overflow:hidden). Very low
          opacity so it reads as a "lighter shade of whatever color the
          card is" rather than competing with the foreground content. */}
      <img
        src={CATEGORY_WATERMARK_URL[category]}
        alt=""
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "105%",
          height: "105%",
          objectFit: "contain",
          opacity: 0.05,
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
        }}
      />
      <div className="relative z-10 flex h-full w-full flex-col p-6 sm:p-7">
        {/* Offense headline — Fraunces serif, the visual anchor. No severity
            label; the card color signals it. */}
        <h3
          className="display"
          style={{
            fontSize: "clamp(22px, 1.9vw, 30px)",
            lineHeight: 1.1,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            fontVariationSettings: '"opsz" 60, "SOFT" 35',
            color: "currentColor",
            wordBreak: "break-word",
          }}
        >
          {/* If we have a humanized version it's already sentence-cased
              correctly (preserves proper nouns); only do the lowercase-
              and-capitalize dance on the raw uppercase fallback. */}
          {rec.human_offense
            ? rec.human_offense
            : offense.charAt(0).toUpperCase() + offense.slice(1).toLowerCase()}
        </h3>

        {/* Bottom strip: court (italic serif), date (mono), status pill.
            Pushed to bottom so cards always look balanced regardless of
            offense-name length. */}
        <div className="mt-auto space-y-3 pt-6">
          {courtRaw && (
            <div
              style={{
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                color: "rgba(255,255,255,0.9)",
                fontSize: "clamp(14px, 1.1vw, 17px)",
                fontVariationSettings: '"opsz" 14, "SOFT" 50',
              }}
            >
              {prettyCourt(courtRaw)}
            </div>
          )}
          {/* Date on left, status (+ optional balance) chips on right.
              `ml-auto` on the right cluster pins it to the right edge
              regardless of whether the date renders, so the disposition
              chip lands in the same spot on every card. */}
          <div className="flex flex-wrap items-center gap-3">
            {dateRaw && (
              <span
                className="mono tabular"
                style={{ color: "rgba(255,255,255,0.75)", fontSize: "clamp(12px, 0.95vw, 14px)" }}
              >
                {shortDate(dateRaw)}
              </span>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {showBalance && (
                isRolledUp ? (
                  // Rolled-up collection account: this $498 is the SAME
                  // balance shown on N tickets. The chip says "shared"
                  // explicitly + lighter visual weight so it doesn't
                  // read as an isolated per-ticket charge.
                  <span
                    className="rounded-full border px-3 py-1 font-semibold uppercase tracking-[0.14em] whitespace-nowrap tabular"
                    style={{
                      background: "rgba(255,255,255,0.18)",
                      color: "rgba(255,255,255,0.95)",
                      borderColor: "rgba(255,255,255,0.45)",
                      borderStyle: "dashed",
                      fontSize: "clamp(10px, 0.8vw, 12px)",
                    }}
                    title={`This ticket is one of ${collectionEntry!.groupSize} in a collection account totaling $${collectionEntry!.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. The balance is shared, paying it once clears the whole account.`}
                  >
                    {`Shared $${collectionEntry!.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} · 1 of ${collectionEntry!.groupSize}`}
                  </span>
                ) : (
                  <span
                    className="rounded-full border px-3 py-1 font-semibold uppercase tracking-[0.14em] whitespace-nowrap tabular"
                    style={{
                      // Solid white-tinted chip so the balance pops against
                      // the severity-colored card background. Different
                      // visual weight from the status pill so it reads as
                      // a separate piece of information at a glance.
                      background: "rgba(255,255,255,0.92)",
                      color: "rgba(0,0,0,0.85)",
                      borderColor: "rgba(255,255,255,0.92)",
                      fontSize: "clamp(10px, 0.8vw, 12px)",
                    }}
                    title="Unpaid balance on this case"
                  >
                    ${balance!.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unpaid
                  </span>
                )
              )}
              {status && (
                <span
                  className="rounded-full border px-3 py-1 font-semibold uppercase tracking-[0.16em] whitespace-nowrap"
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    color: "white",
                    borderColor: "rgba(255,255,255,0.3)",
                    fontSize: "clamp(10px, 0.8vw, 12px)",
                  }}
                >
                  {status}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * "What were they thinking?" — Adams-voiced LLM speculation about the
 * specific human moment behind a specific charge.
 *
 * Strictly lazy: on mount we do ONE cheap GET to check the cache. If a
 * take exists, we render it. If not, we show the prompt-button and DO
 * NOTHING ELSE until the user clicks it. That click fires the (much
 * more expensive) POST that calls the LLM and persists the result.
 *
 * Designed so even at 1M records, the only LLM-billed operations are
 * the ones the user actively asked for.
 */
function RecordTakePanel({
  jobId,
  recordId,
}: {
  jobId: string;
  recordId: number;
}): JSX.Element | null {
  const [take, setTake] = useState<string | null | undefined>(undefined); // undefined = not yet checked
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);

  // ONE cheap cache-check on mount. If nothing is there, take stays null
  // and we render the click-to-generate button.
  useEffect(() => {
    let alive = true;
    getRecordTake(jobId, recordId)
      .then((r) => {
        if (!alive) return;
        setTake(r.take);
        setGeneratedAt(r.generatedAt);
      })
      .catch(() => alive && setTake(null));
    return () => {
      alive = false;
    };
  }, [jobId, recordId]);

  const onGenerate = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await generateRecordTake(jobId, recordId);
      setTake(r.take);
      setGeneratedAt(r.generatedAt);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Initial cache-check still in flight — render nothing (avoid flash).
  if (take === undefined) return null;

  return (
    <div
      className="mb-5 rounded-xl border p-4"
      style={{
        background: "rgba(232,160,80,0.06)",
        borderColor: "rgba(232,160,80,0.25)",
      }}
    >
      <div
        className="flex items-baseline justify-between gap-3 flex-wrap"
        style={{ marginBottom: take ? 10 : 0 }}
      >
        <div
          className="text-[11px] uppercase font-semibold"
          style={{
            color: "var(--color-orange-hi)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.18em",
          }}
        >
          🤔 What were they thinking?
        </div>
        {take && generatedAt && (
          <div
            className="text-[10px]"
            style={{ color: "var(--color-text-dim)", fontFamily: "var(--font-mono)" }}
          >
            cached {new Date(generatedAt).toLocaleDateString()}
            <button
              type="button"
              onClick={onGenerate}
              className="ml-2 underline hover:text-[color:var(--color-orange-hi)]"
              disabled={loading}
              title="Regenerate (uses an LLM call)"
            >
              {loading ? "…" : "regenerate"}
            </button>
          </div>
        )}
      </div>
      {take ? (
        <p
          style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontSize: 17,
            lineHeight: 1.55,
            color: "var(--color-text)",
          }}
        >
          {take}
        </p>
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div
            className="text-[13px]"
            style={{ color: "var(--color-text-muted)", fontFamily: "var(--font-serif)", fontStyle: "italic" }}
          >
            Generate a one-off Adams-voiced speculation about what was
            actually going through their head when they did this. Cached
            after first use.
          </div>
          <button
            type="button"
            onClick={onGenerate}
            disabled={loading}
            className="rounded-lg px-4 py-2 text-[12px] font-bold transition disabled:opacity-50"
            style={{
              background: "var(--color-orange-hi)",
              color: "white",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.08em",
              whiteSpace: "nowrap",
            }}
          >
            {loading ? "Speculating…" : "GENERATE TAKE →"}
          </button>
        </div>
      )}
      {error && (
        <div
          className="mt-2 text-[12px]"
          style={{ color: "var(--color-rose-hi)", fontFamily: "var(--font-mono)" }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/* Modal that pops over the page when a card is clicked. Click outside or
   Esc to close. Body scrolls inside the modal — page itself stays put. */
function OffenseModal({
  rec,
  jobId,
  onClose,
}: {
  rec: RecordRow;
  jobId: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const data = rec.data;
  if (!isRecordData(data)) return null;

  const rawOffense =
    pick(data.summary, "Offense Description") || pick(data.summary, "Offense") || "—";
  // Match OffenseCard: prefer the LLM-translated "human_offense" (which
  // correctly preserves proper nouns like "Paterson Municipal Court"),
  // falling back to a sentence-cased version of the raw uppercase
  // court text only when no humanized rephrasing is available. The
  // earlier modal code did `slice(1).toLowerCase()` on the raw text
  // unconditionally — destroying proper nouns and ignoring the more
  // human translation already on the record.
  const offense = rawOffense;
  const dateRaw = pickRecordDate(data.summary);
  const courtRaw = pick(data.summary, "Court");
  const status = recordStatus(data.summary);
  // Severity classifier expects the raw uppercase text (POSS CDS, SIMPLE
  // ASSAULT, etc.) — not the humanized rephrasing. Mirrors the card.
  const severity = classifyOffense(rawOffense);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center backdrop-blur-sm fade-in p-0 sm:items-center sm:p-6"
      style={{ background: "rgba(8,12,18,0.72)" }}
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl border border-[color:var(--color-line)] bg-[color:var(--color-bg)] sm:max-w-3xl sm:rounded-2xl lg:max-w-5xl xl:max-w-6xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header strip — solid severity color, name + close button */}
        <div
          className="severity-tile relative px-6 py-6 sm:px-8 sm:py-7"
          style={{ background: severityGradient(severity), color: severityInk() }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white transition hover:bg-white/25"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.24em]"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            {SEVERITY_LABEL[severity]}
          </div>
          <h2
            className="display mt-2"
            style={{
              fontSize: "clamp(24px, 3vw, 36px)",
              lineHeight: 1.1,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              fontVariationSettings: '"opsz" 96, "SOFT" 40',
              color: "white",
              paddingRight: "2.5rem",
            }}
          >
            {/* If we have a humanized version it's already sentence-cased
                correctly (preserves proper nouns); only do the lowercase-
                and-capitalize dance on the raw uppercase fallback. */}
            {rec.human_offense
              ? rec.human_offense
              : offense.charAt(0).toUpperCase() + offense.slice(1).toLowerCase()}
          </h2>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px]" style={{ color: "rgba(255,255,255,0.85)" }}>
            {courtRaw && (
              <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
                {prettyCourt(courtRaw)}
              </span>
            )}
            {dateRaw && <span className="mono tabular">{displayDate(dateRaw)}</span>}
            {status && (
              <span
                className="rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]"
                style={{
                  background: "rgba(255,255,255,0.18)",
                  color: "white",
                  borderColor: "rgba(255,255,255,0.3)",
                }}
              >
                {status}
              </span>
            )}
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto p-6 sm:p-8">
          {/* Adams-voiced LLM hot-take panel. Lazy: nothing happens
              until the user explicitly clicks. Once generated, cached
              forever in record_takes (so refreshes are free). */}
          <RecordTakePanel jobId={jobId} recordId={rec.id} />
          <FullRecordView data={data} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function FullRecordView({ data }: { data: RecordData }) {
  return (
    <div className="space-y-5">
      <FieldGroups fields={data.summary} />
      {data.subPages.length > 0 && (
        <div className="space-y-3">
          {data.subPages.map((sp, i) => (
            <details
              key={i}
              open={i === 0}
              className="rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-bg-alt)] p-4"
            >
              <summary className="eyebrow cursor-pointer">{sp.kind}</summary>
              <div className="mt-4">
                <FieldGroups fields={sp.fields} />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldGroups({ fields }: { fields: Record<string, string | string[]> }) {
  const groups = groupFields(fields);
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <div key={g.key}>
          <div className="mb-3 flex items-baseline gap-2">
            <div className="eyebrow text-[10px]">{g.label}</div>
            {g.hint && <div className="text-[10px] text-[color:var(--color-text-dim)]">({g.hint})</div>}
          </div>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {g.entries.map(([k, v]) => {
              const val = Array.isArray(v) ? v.join(" · ") : v;
              const isNumeric = /\d/.test(val);
              return (
                <div
                  key={k}
                  className="flex flex-col border-b border-dotted border-[color:var(--color-line)] pb-2"
                >
                  <dt className="text-[10px] font-medium uppercase tracking-wider text-[color:var(--color-text-dim)]">
                    {k}
                  </dt>
                  <dd
                    className={`mt-0.5 text-sm text-[color:var(--color-text)] ${isNumeric ? "tabular" : ""}`}
                  >
                    {val}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// RecordsEmptyState — what the records section renders when there are
// zero records to show. Two distinct cases handled:
//
//   1. MCCS hasn't finished yet (queued / running / waiting_captcha):
//      shows a "Search in progress…" card so the user knows results
//      may still appear.
//   2. MCCS finished with zero rows: shows a prominent "Municipal
//      court search complete — 0 records on file" card with the
//      timestamp + a DOB CONFIRM strip. The DOB confirmation is the
//      cure for Damon's bug — the case where MCCS searched a wrong
//      DOB and correctly returned no hits, but the page didn't
//      surface enough context for him to suspect the data-entry.
//   3. MCCS errored: shows an error card with the message.
//
// All three replace the old "No records." faint gray text, which
// ambiguously read as either "we haven't searched" or "the page is
// broken." This card always answers "did the search run, and when?"
// ─────────────────────────────────────────────────────────────────────────
function RecordsEmptyState({
  job,
  unfilteredRecordCount,
}: {
  job: JobRow;
  /** When >0, the user is filtering and there are records — but the
   *  current filter hides them all. We render a different copy for
   *  this case so the user knows to clear the filter, not re-run. */
  unfilteredRecordCount: number;
}): JSX.Element {
  const isRunning = job.status === "queued" || job.status === "running" || job.status === "waiting_captcha";
  const isError = job.status === "error";
  const isFiltered = unfilteredRecordCount > 0;
  const completedAt = job.completed_at ? new Date(job.completed_at) : null;
  const expected = job.search_result_count ?? null;
  const captured = job.record_count_captured ?? 0;

  // Filter case: there ARE records, the active filter just hides them all.
  if (isFiltered) {
    return (
      <div
        className="card p-6 text-center"
        style={{
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-line)",
        }}
      >
        <div className="text-[14px] font-semibold" style={{ color: "var(--color-text)" }}>
          {unfilteredRecordCount} record{unfilteredRecordCount === 1 ? "" : "s"} on file,
          all hidden by the current filter.
        </div>
        <div className="text-[12px] mt-1" style={{ color: "var(--color-text-muted)" }}>
          Clear the filter in the dock at the bottom of the screen to see them.
        </div>
      </div>
    );
  }

  // Running case
  if (isRunning) {
    return (
      <div
        className="card p-6"
        style={{
          background: "var(--color-bg-card)",
          border: "1px solid color-mix(in oklab, var(--color-orange-hi) 35%, var(--color-line))",
        }}
      >
        <div className="flex items-baseline gap-3 flex-wrap">
          <span
            className="dot dot-pulse"
            style={{ background: "var(--color-orange-hi)", flexShrink: 0 }}
            aria-hidden="true"
          />
          <div
            className="text-[15px] font-semibold"
            style={{ color: "var(--color-text)" }}
          >
            Municipal court search in progress…
          </div>
        </div>
        <div className="text-[13px] mt-2" style={{ color: "var(--color-text-muted)" }}>
          {job.step ?? "Working through the search results."}
          {expected != null && expected > 0 && captured >= 0 && (
            <span> · {captured} of {expected} captured so far</span>
          )}
        </div>
      </div>
    );
  }

  // Error case
  if (isError) {
    return (
      <div
        className="card p-6"
        style={{
          background: "var(--color-bg-card)",
          border: "1px solid color-mix(in oklab, var(--color-rose-hi) 50%, var(--color-line))",
        }}
      >
        <div
          className="text-[15px] font-semibold"
          style={{ color: "var(--color-rose-hi)" }}
        >
          Municipal court search errored.
        </div>
        <div className="text-[13px] mt-2" style={{ color: "var(--color-text-muted)" }}>
          {job.error_message ?? "The search did not complete."}{" "}
          Use the dock to retry the scrape.
        </div>
      </div>
    );
  }

  // Default: completed, zero records. The "clean record" case — but we
  // emphasize the inputs we searched on (name + DOB) so the user can
  // immediately verify a typo wasn't the cause of the empty result.
  return (
    <div
      className="card p-6"
      style={{
        background: "var(--color-bg-card)",
        border: "1px solid color-mix(in oklab, var(--color-sage-hi) 45%, var(--color-line))",
      }}
    >
      <div className="flex items-baseline gap-3 flex-wrap">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            fontSize: 16,
            color: "var(--color-sage-hi)",
            fontWeight: 700,
          }}
        >
          ✓
        </span>
        <div
          className="text-[15px] font-semibold"
          style={{ color: "var(--color-text)" }}
        >
          Municipal court search complete, no records on file.
        </div>
      </div>

      {/* Inputs strip — promoted ABOVE the explanation paragraph so the
          first thing the user sees is the exact inputs the search ran
          against. The whole point of this empty-state card is to make
          a name/DOB typo easy to catch; burying these below the copy
          (the previous order) hid the diagnostic data. Bumped font
          size 12 → 14 for the same reason. */}
      <div
        className="mt-3 inline-flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 rounded"
        style={{
          background: "var(--color-bg-card-hi)",
          border: "1px solid var(--color-line-hi)",
          fontFamily: "var(--font-mono)",
          fontSize: 14,
          letterSpacing: "0.02em",
        }}
      >
        <span>
          <span style={{ color: "var(--color-text-dim)" }}>Searched as:</span>{" "}
          <strong style={{ color: "var(--color-text)" }}>
            {job.first_name} {job.last_name}
          </strong>
        </span>
        <span>
          <span style={{ color: "var(--color-text-dim)" }}>DOB:</span>{" "}
          <strong style={{ color: "var(--color-text)" }}>
            {job.date_of_birth}
          </strong>
        </span>
      </div>

      <div
        className="text-[13px] mt-3"
        style={{ color: "var(--color-text-muted)", lineHeight: 1.55 }}
      >
        We ran the MCCS lookup against the inputs above and the site returned
        zero results. If you expected records here, the most common cause is
        a name or DOB typo, verify and re-run if needed.
      </div>

      {completedAt && (
        <div
          className="text-[11px] mt-3"
          style={{ color: "var(--color-text-dim)" }}
        >
          Search ran {completedAt.toLocaleString()}.
        </div>
      )}
    </div>
  );
}

function CardsView({ records, jobId }: { records: RecordRow[]; jobId: string }) {
  // Click a card → set as active → modal opens. Modal closes via overlay
  // click / Esc / its own ✕ button. State lives at the grid level so the
  // active card is shared across siblings.
  const [activeId, setActiveId] = useState<number | null>(null);
  const activeRecord = records.find((r) => r.id === activeId) ?? null;

  // Detect rolled-up collection accounts (multiple records sharing the
  // same court+balance signature). Each entry tells the card whether
  // its balance is part of a multi-ticket account so we can render the
  // chip as "shared $X · 1 of N" instead of misleading per-card $X.
  const collectionMap = useMemo(() => buildCollectionMap(records), [records]);

  // Empty state is rendered ONE LEVEL UP (in PersonPage) so it has the
  // full job context (status / completed_at / error_message etc.) to
  // build a proper "MCCS search complete — 0 records on file" card.
  // CardsView and TableView only render when records.length > 0.

  return (
    <>
      {/* Responsive grid: 1 col on phones, 2 on small tablets, 3 on laptops,
          4 on regular desktops, 5 on big screens. The card heights are set
          via min-height + auto-rows-fr so every tile in the grid renders at
          identical size — uniform sheet of color across the whole page. */}
      <div className="grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 md:grid-cols-3 lg:gap-6 xl:grid-cols-4 2xl:grid-cols-5">
        {records.map((r) => (
          <OffenseCard
            key={r.id}
            rec={r}
            onOpen={() => setActiveId(r.id)}
            collectionEntry={collectionMap.get(r.id) ?? null}
          />
        ))}
      </div>
      {activeRecord && <OffenseModal rec={activeRecord} jobId={jobId} onClose={() => setActiveId(null)} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Table view
// ─────────────────────────────────────────────────────────────────────────

/* Map a status string to a chip color. Active / time-payment / warrant /
   open all surface as warning; disposed / recalled / satisfied surface as
   neutral-success. Unknown stays default. */
function statusChipStyle(status: string): { bg: string; color: string; border: string } {
  const s = status.toLowerCase();
  if (/warrant/.test(s)) return { bg: "color-mix(in oklab, var(--color-rose-hi) 18%, transparent)", color: "var(--color-rose-hi)", border: "color-mix(in oklab, var(--color-rose-hi) 40%, transparent)" };
  if (/active|pending/.test(s)) return { bg: "color-mix(in oklab, var(--color-orange-hi) 18%, transparent)", color: "var(--color-orange-hi)", border: "color-mix(in oklab, var(--color-orange-hi) 40%, transparent)" };
  if (/time\s*payment|fine/.test(s)) return { bg: "color-mix(in oklab, var(--color-gold-hi) 18%, transparent)", color: "var(--color-gold-hi)", border: "color-mix(in oklab, var(--color-gold-hi) 40%, transparent)" };
  if (/disposed|recalled|satisfied|closed/.test(s)) return { bg: "color-mix(in oklab, var(--color-sage-hi) 16%, transparent)", color: "var(--color-sage-hi)", border: "color-mix(in oklab, var(--color-sage-hi) 35%, transparent)" };
  return { bg: "var(--color-bg-card-hi)", color: "var(--color-text-muted)", border: "var(--color-line)" };
}

type SortKey = "date" | "offense" | "court" | "status" | "severity";
type SortDir = "asc" | "desc";

const SEVERITY_RANK: Record<string, number> = { minor: 0, moderate: 1, serious: 2, severe: 3 };

function TableView({ records }: { records: RecordRow[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Same collection-account detection as CardsView. Records that share a
  // rolled-up balance get rendered as "shared $X · 1 of N" so the table
  // doesn't multiply the same balance across N rows in the user's head.
  const collectionMap = useMemo(() => buildCollectionMap(records), [records]);

  // Decorate records once with the values we sort + render against, so the
  // sort comparator stays simple and deterministic.
  const decorated = useMemo(() => {
    return records
      .filter((r) => isRecordData(r.data))
      .map((r) => {
        const data = r.data as RecordData;
        const offense =
          pick(data.summary, "Offense Description") || pick(data.summary, "Offense") || "—";
        const offenseCode = pick(data.summary, "Offense");
        const dateRaw = pickRecordDate(data.summary);
        // Sort key: prefer a full MM/DD/YYYY, then a year-only fallback
        // (sorts to Jan 1 of that year), then 0 so undated rows sink.
        const fullDate = dateRaw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        const yearOnly = !fullDate && /^\d{4}$/.test(dateRaw) ? dateRaw : null;
        const dateMs = fullDate && fullDate[1] && fullDate[2] && fullDate[3]
          ? new Date(`${fullDate[3]}-${fullDate[1].padStart(2, "0")}-${fullDate[2].padStart(2, "0")}`).getTime()
          : yearOnly
            ? new Date(`${yearOnly}-01-01`).getTime()
            : 0;
        const court = pick(data.summary, "Court");
        const status = recordStatus(data.summary) || "—";
        const severity = classifyOffense(offense);
        // Unpaid balance — null when no parseable balance, 0 when paid
        // in full, > 0 when there's still money owed. Only displayed
        // for OPEN cases where the value is > 0 (paid + closed cases
        // both render "—" so the column doesn't shout zero amounts).
        const balance = recordBalance(r);
        const showBalance =
          isOpenStatus(recordStatus(data.summary)) &&
          typeof balance === "number" &&
          balance > 0;
        const collectionEntry = collectionMap.get(r.id) ?? null;
        return {
          rec: r,
          data,
          offense,
          offenseCode,
          dateRaw,
          dateMs,
          court,
          courtPretty: court ? prettyCourt(court) : "",
          status,
          collectionEntry,
          severity,
          balance,
          showBalance,
        };
      });
  }, [records]);

  // Only show the Unpaid column when at least one row has a balance to
  // surface. Otherwise it'd be a column full of "—" wasting horizontal
  // space — particularly painful on narrower screens.
  const showUnpaidCol = useMemo(
    () => decorated.some((d) => d.showBalance),
    [decorated],
  );

  const sorted = useMemo(() => {
    const arr = [...decorated];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "date":     cmp = a.dateMs - b.dateMs; break;
        case "offense":  cmp = a.offense.localeCompare(b.offense); break;
        case "court":    cmp = a.courtPretty.localeCompare(b.courtPretty); break;
        case "status":   cmp = a.status.localeCompare(b.status); break;
        case "severity": cmp = (SEVERITY_RANK[a.severity] ?? 0) - (SEVERITY_RANK[b.severity] ?? 0); break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [decorated, sortKey, sortDir]);

  const onHeaderClick = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "date" ? "desc" : "asc"); }
  };

  // Empty state lifted to PersonPage — see CardsView for explanation.

  // Sortable column header. Click toggles direction; clicking a different
  // column resets to a sensible default (descending for date, ascending
  // for everything else).
  const HeaderCell = ({
    k,
    label,
    align = "left",
  }: {
    k: SortKey;
    label: string;
    align?: "left" | "right";
  }) => (
    <th
      onClick={() => onHeaderClick(k)}
      scope="col"
      className="select-none cursor-pointer transition-colors hover:text-[color:var(--color-text)]"
      style={{
        padding: "10px 12px",
        textAlign: align,
        color: sortKey === k ? "var(--color-text)" : "var(--color-text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        borderBottom: "1px solid var(--color-line)",
        background: "var(--color-bg-card-hi)",
      }}
      title={`Sort by ${label}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <span
          aria-hidden="true"
          style={{
            fontSize: 9,
            opacity: sortKey === k ? 1 : 0.25,
            color: sortKey === k ? "var(--color-text)" : "currentColor",
          }}
        >
          {sortKey === k ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </span>
    </th>
  );

  // Real <table> with sortable headers. Each record renders as its own
  // <tbody> containing TWO rows: a structured columnar row (date / court
  // / severity / status / statute) on top, and a colspan'd offense-
  // description row below that gets the FULL table width — so long
  // 50-char descriptions flow on one or two lines without crushing
  // sibling columns. Per-record tbody also makes click-to-expand insert
  // the FullRecordView as a clean third row inside the same group.

  return (
    <div className="card overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ tableLayout: "fixed", minWidth: 600 }}>
          {/* Fixed column widths sized to actual data: date max ~16 chars,
              court ~28, severity 8, status 12, statute 12. The offense row
              ignores these widths via colspan, so long descriptions get
              the full width. */}
          <colgroup>
            <col style={{ width: 110 }} />               {/* Date */}
            <col />                                      {/* Court (flex) */}
            <col style={{ width: 110 }} />               {/* Severity */}
            <col style={{ width: 130 }} />               {/* Status */}
            {showUnpaidCol && (
              <col style={{ width: 110 }} className="hidden md:table-column" /> /* Unpaid (md+) */
            )}
            <col style={{ width: 110 }} className="hidden sm:table-column" /> {/* Statute */}
          </colgroup>
          <thead>
            <tr>
              <HeaderCell k="date" label="Date" />
              <HeaderCell k="court" label="Court" />
              <HeaderCell k="severity" label="Severity" />
              <HeaderCell k="status" label="Status" />
              {showUnpaidCol && (
                <th
                  scope="col"
                  className="hidden md:table-cell"
                  style={{
                    padding: "10px 12px",
                    textAlign: "right",
                    color: "var(--color-text-muted)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid var(--color-line)",
                    background: "var(--color-bg-card-hi)",
                  }}
                  title="Outstanding balance owed on open cases"
                >
                  Unpaid
                </th>
              )}
              <th
                scope="col"
                className="hidden sm:table-cell"
                style={{
                  padding: "10px 12px",
                  textAlign: "left",
                  color: "var(--color-text-muted)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid var(--color-line)",
                  background: "var(--color-bg-card-hi)",
                }}
              >
                Statute
              </th>
            </tr>
          </thead>

          {sorted.map((row) => {
            const { rec: r, data, offense, offenseCode, dateRaw, courtPretty, status, severity, balance, showBalance, collectionEntry } = row;
            // Treat the per-row balance as "shared" when this record is
            // part of a multi-record collection account (Tarquino's 30
            // parking tickets all showing the same $498 rolled-up
            // balance). The cell shows "$498*" with a tooltip explaining
            // it's shared, instead of multiplying $498 in the reader's
            // mental tally.
            const isRolledUp = collectionEntry?.isCollectionGroup ?? false;
            const isOpen = expanded === r.id;
            const chip = statusChipStyle(status);
            const accent = severityAccent(severity);

            return (
              <tbody
                key={r.id}
                onClick={() => setExpanded(isOpen ? null : r.id)}
                className="cursor-pointer transition-colors hover:bg-[color:var(--color-bg-card-hi)]"
                style={{
                  borderBottom: "1px solid var(--color-line)",
                  // Severity-colored left accent on every record group.
                  // Applied via box-shadow so it stays inside the cell
                  // without disrupting table layout.
                  boxShadow: `inset 3px 0 0 0 ${accent}`,
                }}
              >
                {/* Row 1: structured columnar data */}
                <tr>
                  <td
                    className="tabular"
                    style={{
                      padding: "12px 12px 4px 16px",
                      whiteSpace: "nowrap",
                      color: "var(--color-text)",
                      fontFamily: "var(--font-mono)",
                      fontSize: 13,
                      fontWeight: 500,
                      verticalAlign: "baseline",
                    }}
                  >
                    {displayDate(dateRaw)}
                  </td>
                  <td
                    style={{
                      padding: "12px 12px 4px",
                      color: "var(--color-text-muted)",
                      fontFamily: "var(--font-serif)",
                      fontSize: 13,
                      fontStyle: "italic",
                      verticalAlign: "baseline",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={courtPretty || ""}
                  >
                    {courtPretty || <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td
                    style={{
                      padding: "12px 12px 4px",
                      verticalAlign: "baseline",
                    }}
                  >
                    <span
                      className={`font-semibold uppercase ${SEVERITY_TEXT[severity]}`}
                      style={{
                        fontSize: 11,
                        letterSpacing: "0.16em",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {SEVERITY_LABEL[severity]}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "12px 12px 4px",
                      verticalAlign: "baseline",
                    }}
                  >
                    <span
                      className="rounded-full border font-semibold uppercase whitespace-nowrap"
                      style={{
                        background: chip.bg,
                        color: chip.color,
                        borderColor: chip.border,
                        fontSize: 10,
                        letterSpacing: "0.14em",
                        padding: "2px 9px",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {status}
                    </span>
                  </td>
                  {showUnpaidCol && (
                    <td
                      className="hidden md:table-cell tabular"
                      style={{
                        padding: "12px 12px 4px",
                        whiteSpace: "nowrap",
                        textAlign: "right",
                        // Rolled-up rows render dim + dashed-italic so they
                        // don't visually accumulate in a column-scan; the
                        // "*" hints that the value isn't a per-row charge.
                        color: !showBalance
                          ? "var(--color-text-dim)"
                          : isRolledUp
                            ? "rgba(255,209,102,0.55)"
                            : "var(--color-amber-hi, #ffd166)",
                        fontStyle: isRolledUp ? "italic" : "normal",
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                        fontWeight: showBalance && !isRolledUp ? 600 : 400,
                        verticalAlign: "baseline",
                      }}
                      title={
                        !showBalance
                          ? undefined
                          : isRolledUp
                            ? `Shared balance: this is one of ${collectionEntry!.groupSize} tickets in a collection account totaling $${collectionEntry!.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Counted once in the page total.`
                            : "Unpaid balance"
                      }
                    >
                      {showBalance && typeof balance === "number"
                        ? `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${isRolledUp ? "*" : ""}`
                        : "—"}
                    </td>
                  )}
                  <td
                    className="hidden sm:table-cell"
                    style={{
                      padding: "12px 16px 4px 12px",
                      whiteSpace: "nowrap",
                      color: "var(--color-text-muted)",
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      verticalAlign: "baseline",
                    }}
                  >
                    {offenseCode || <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                </tr>

                {/* Row 2: offense description spanning the full table width.
                    colSpan dynamically tracks whether the Unpaid column is
                    present so the description always spans every visible
                    column without a gap on the right. */}
                <tr>
                  <td
                    colSpan={showUnpaidCol ? 6 : 5}
                    style={{
                      padding: "0 16px 14px 16px",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontWeight: 500,
                        fontSize: "clamp(15px, 1.3vw, 19px)",
                        lineHeight: 1.35,
                        color: "var(--color-text)",
                        fontVariationSettings: '"opsz" 36, "SOFT" 50',
                        overflowWrap: "anywhere",
                      }}
                    >
                      {offense}
                      <span
                        aria-hidden="true"
                        style={{
                          float: "right",
                          color: "var(--color-text-dim)",
                          fontStyle: "normal",
                          fontSize: 14,
                          marginLeft: 8,
                          transition: "transform 0.15s",
                          display: "inline-block",
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      >
                        ⌄
                      </span>
                    </div>
                  </td>
                </tr>

                {/* Row 3 (conditional): expanded full record details */}
                {isOpen && (
                  <tr>
                    <td
                      colSpan={showUnpaidCol ? 6 : 5}
                      style={{
                        padding: "12px 16px 16px 16px",
                        background: "var(--color-bg-alt)",
                        borderTop: "1px solid var(--color-line)",
                        overflowWrap: "anywhere",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FullRecordView data={data} />
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </div>

      {/* Footer count — small caption-row beneath the table */}
      <div
        style={{
          padding: "10px 16px",
          background: "var(--color-bg-card-hi)",
          borderTop: "1px solid var(--color-line)",
          color: "var(--color-text-dim)",
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontSize: 12,
          textAlign: "right",
        }}
      >
        {sorted.length} record{sorted.length === 1 ? "" : "s"}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Screenshots view
// ─────────────────────────────────────────────────────────────────────────

function ScreenshotsView({
  shots,
  jobId,
  openOrdinals,
  activeSource,
  onActiveSourceChange,
  openOnly,
  onOpenOnlyChange,
}: {
  shots: ScreenshotRow[];
  jobId: string;
  /** Ordinals of MPA records that are still "open" (not Disposed/Recalled/etc).
   *  Drives the "open only" toggle. Non-MPA screenshots aren't gated by
   *  this since we don't carry per-record status for those sources. */
  openOrdinals: Set<number>;
  /** Controlled-from-parent so the floating dock's filter chip and the
   *  in-page source pills stay in sync. */
  activeSource: string | "all";
  onActiveSourceChange: (k: string | "all") => void;
  openOnly: boolean;
  onOpenOnlyChange: (v: boolean) => void;
}) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [showDuplicates, setShowDuplicates] = useState(false);

  // Group screenshots by their source scraper (mpa/doc/pgpa/jocpa/bop)
  // so the user can see at a glance how many screenshots came from each
  // and filter to one source at a time. Filtering runs BEFORE the
  // dedup pass so per-source counts are accurate.
  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of shots) {
      const src = s.source || "mpa";
      counts.set(src, (counts.get(src) ?? 0) + 1);
    }
    return counts;
  }, [shots]);

  // How many screenshots WOULD survive the open-only filter? Drives the
  // toggle's count badge so the user knows what they're about to see.
  const openShotCount = useMemo(() => {
    return shots.filter((s) => {
      const src = s.source || "mpa";
      if (src !== "mpa") return true; // non-MPA screenshots always pass
      return s.ordinal !== null && openOrdinals.has(s.ordinal);
    }).length;
  }, [shots, openOrdinals]);

  const filteredShots = useMemo(() => {
    let out = shots;
    if (activeSource !== "all") {
      out = out.filter((s) => (s.source || "mpa") === activeSource);
    }
    if (openOnly) {
      out = out.filter((s) => {
        const src = s.source || "mpa";
        if (src !== "mpa") return true;
        return s.ordinal !== null && openOrdinals.has(s.ordinal);
      });
    }
    return out;
  }, [shots, activeSource, openOnly, openOrdinals]);

  // Sort first, then collapse byte-identical duplicates (keep the earliest
  // representative). PGPA's flow re-screenshots the same search-results
  // page on every drill-in, producing N byte-identical copies; this hides
  // them without touching the underlying files.
  const sorted = useMemo(() => {
    const order: Record<ScreenshotRow["kind"], number> = {
      search_results: 0,
      detail: 1,
      // Time Payment + Bail tabs sit just after the case detail because
      // they're contextual sub-tabs of the Additional Details page.
      time_payment: 1.3,
      bail: 1.5,
      sentence: 1.7,
      mugshot: 2,
      error: 3,
      captcha: 4,
    };
    return [...filteredShots].sort(
      (a, b) =>
        order[a.kind] - order[b.kind] ||
        (a.ordinal ?? -1) - (b.ordinal ?? -1) ||
        a.created_at.localeCompare(b.created_at),
    );
  }, [filteredShots]);

  const { deduped, hiddenCount } = useMemo(() => {
    if (showDuplicates) {
      return {
        deduped: sorted.map((s) => ({ shot: s, dupCount: 1 })),
        hiddenCount: 0,
      };
    }
    const groups = new Map<string, { shot: ScreenshotRow; dupCount: number }>();
    const out: Array<{ shot: ScreenshotRow; dupCount: number }> = [];
    let hidden = 0;
    for (const s of sorted) {
      // Rows without a hash (legacy, or a fingerprint failure) always pass
      // through — they're treated as unique. Otherwise group by hash.
      const key = s.bytes_hash ?? `__nohash:${s.id}`;
      const existing = groups.get(key);
      if (existing) {
        existing.dupCount += 1;
        hidden += 1;
      } else {
        const entry = { shot: s, dupCount: 1 };
        groups.set(key, entry);
        out.push(entry);
      }
    }
    return { deduped: out, hiddenCount: hidden };
  }, [sorted, showDuplicates]);

  if (shots.length === 0)
    return <div className="card text-center text-sm text-[color:var(--color-text-dim)] py-8">No screenshots.</div>;

  const lightboxList = deduped;
  const current = lightboxIdx !== null ? lightboxList[lightboxIdx] : null;

  // Source tab definitions, in display order. Only show tabs that actually
  // have screenshots — no point listing BOP if nothing's there.
  // IMPORTANT: every ExternalSource must be listed here, otherwise its
  // screenshots have no per-source tab and can only be seen in "All".
  // (That was the William-Turner-missing-MVC bug before this fix.)
  const SOURCE_LABELS: Record<string, string> = {
    mpa: "Municipal",
    pgpa: "Superior",
    jocpa: "Judgments",
    doc: "DOC",
    "mvc-surcharge": "MVC Surcharge",
    "mvc-restoration": "MVC Restoration",
  };
  const sourceTabs: Array<{ key: string; label: string; count: number }> = [
    { key: "all", label: "All", count: shots.length },
    ...Object.keys(SOURCE_LABELS)
      .filter((k) => (sourceCounts.get(k) ?? 0) > 0)
      .map((k) => ({ key: k, label: SOURCE_LABELS[k]!, count: sourceCounts.get(k)! })),
  ];

  // PDF packet endpoint. "all" downloads everything; per-source filters
  // by the source query param. The Open-only toggle (when active) is
  // also passed through so the packet matches what the user sees on
  // screen. Filename includes the person + scope so multiple downloads
  // don't collide.
  const packetHref = (() => {
    const params = new URLSearchParams();
    if (activeSource !== "all") params.set("source", activeSource);
    if (openOnly) params.set("openOnly", "1");
    const qs = params.toString();
    return `/api/jobs/${jobId}/screenshots-packet.pdf${qs ? `?${qs}` : ""}`;
  })();

  return (
    <>
      {/* Source tabs + Download PDF packet button */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap rounded-xl border border-[color:var(--color-line)] p-1">
          {sourceTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => onActiveSourceChange(t.key)}
              className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition ${
                activeSource === t.key
                  ? "bg-[color:var(--color-bg-card-hi)] text-[color:var(--color-text)]"
                  : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
              }`}
            >
              {t.label}
              <span className="text-[11px] tabular text-[color:var(--color-text-dim)]">{t.count}</span>
            </button>
          ))}
        </div>
        {/* Open-only toggle — mirrors the Cards/Table version. Only shown
            when at least one MPA record is open (otherwise the toggle is
            a no-op). Hidden when the user is on a non-MPA source tab too,
            since the filter doesn't change anything there. */}
        {openOrdinals.size > 0 && openOrdinals.size < sourceCounts.get("mpa")! && (activeSource === "all" || activeSource === "mpa") && (
          <button
            type="button"
            onClick={() => onOpenOnlyChange(!openOnly)}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-[13px] font-semibold transition ${
              openOnly
                ? "border-[color:var(--color-orange-hi)] bg-[color:var(--color-orange-soft)] text-[color:var(--color-orange-hi)]"
                : "border-[color:var(--color-line)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
            }`}
            title={
              openOnly
                ? `Showing ${openShotCount} screenshots from outstanding MCCS cases (everything except Disposed and Transferred to). Click to show all.`
                : `Limit to outstanding MCCS cases — hides Disposed and Transferred-to records (${openShotCount} of ${shots.length}).`
            }
          >
            <span
              aria-hidden="true"
              className="inline-flex h-2.5 w-2.5 items-center justify-center rounded-full"
              style={{
                background: openOnly ? "var(--color-orange-hi)" : "transparent",
                border: openOnly ? "none" : "1.5px solid currentColor",
              }}
            />
            <span>Outstanding only</span>
            <span className="tabular text-[12px] opacity-70">
              {openOnly ? `${openShotCount}` : `${openShotCount}/${shots.length}`}
            </span>
          </button>
        )}

        <a
          href={packetHref}
          download
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-semibold transition hover:-translate-y-0.5"
          style={{
            background: "var(--color-bg-card-hi)",
            color: "var(--color-text)",
            border: "1px dashed color-mix(in oklab, var(--color-amber-hi, #d6a455) 50%, transparent)",
          }}
          title={
            activeSource === "all"
              ? "Download a PDF packet of EVERY screenshot for this person, grouped by source"
              : `Download a PDF packet of just the ${SOURCE_LABELS[activeSource] ?? activeSource} screenshots`
          }
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 2v9m0 0L4.5 7.5M8 11l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 13.5h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span>
            {activeSource === "all" ? "Download PDF packet" : `Download ${SOURCE_LABELS[activeSource] ?? activeSource} packet`}
          </span>
        </a>
      </div>

      {hiddenCount > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-bg-card)] px-3 py-2 text-[12px] text-[color:var(--color-text-muted)]">
          <span>
            Hiding <strong className="text-[color:var(--color-text)]">{hiddenCount}</strong>{" "}
            byte-identical {hiddenCount === 1 ? "duplicate" : "duplicates"}.
          </span>
          <button
            type="button"
            onClick={() => setShowDuplicates(true)}
            className="text-[color:var(--color-text)] underline decoration-dotted underline-offset-2 hover:text-white"
          >
            Show all
          </button>
        </div>
      )}
      {showDuplicates && (
        <div className="mb-3 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setShowDuplicates(false)}
            className="text-[12px] text-[color:var(--color-text-muted)] underline decoration-dotted underline-offset-2 hover:text-white"
          >
            Collapse duplicates
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
        {deduped.map(({ shot: s, dupCount }, i) => (
          <button
            key={s.id}
            onClick={() => setLightboxIdx(i)}
            className="group relative block overflow-hidden rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-bg-card)] text-left transition hover:border-[color:var(--color-line-hi)] hover:shadow-lg"
          >
            <img
              src={`/api/screenshots/${s.id}`}
              alt={s.kind}
              loading="lazy"
              onError={handleShotError}
              className="h-32 w-full object-cover object-top transition group-hover:opacity-95 sm:h-40"
            />
            {dupCount > 1 && (
              <span
                className="absolute right-2 top-2 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular text-white backdrop-blur-sm"
                style={{ background: "rgba(8,12,18,0.65)" }}
                title={`${dupCount} byte-identical copies of this screenshot`}
              >
                ×{dupCount}
              </span>
            )}
            {/* Synth badge — for screenshots rendered from raw scrape
                data rather than captured from a live browser page (e.g.
                BOP, which is a JSON API). The hover tooltip explains
                what that means so the user knows this isn't an
                evidence-grade snapshot of an actual website. */}
            {s.is_synth ? (
              <span
                className="absolute left-2 top-2 rounded bg-orange-600/80 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white backdrop-blur-sm"
                title="Recreated from raw scrape data, this source returns JSON only, so we render the data as HTML and screenshot that. Trust the underlying data, not the visual layout."
                style={{ letterSpacing: "0.12em" }}
              >
                SYNTH
              </span>
            ) : null}
            <div className="flex items-center justify-between p-2">
              <div className="meta capitalize text-[color:var(--color-text-muted)]">
                {s.kind.replace("_", " ")}
              </div>
              <div className="meta tabular text-[color:var(--color-text-dim)]">
                {s.ordinal !== null ? `#${s.ordinal + 1}` : ""}
              </div>
            </div>
          </button>
        ))}
      </div>
      {current && (
        <Lightbox
          src={`/api/screenshots/${current.shot.id}`}
          caption={`${current.shot.kind} ${current.shot.ordinal !== null ? `#${(current.shot.ordinal ?? 0) + 1}` : ""}${current.dupCount > 1 ? ` (×${current.dupCount} identical copies)` : ""}${current.shot.is_synth ? " · recreated from raw scrape data" : ""}`}
          onClose={() => setLightboxIdx(null)}
          onPrev={lightboxIdx !== null && lightboxIdx > 0 ? () => setLightboxIdx(lightboxIdx - 1) : null}
          onNext={lightboxIdx !== null && lightboxIdx < lightboxList.length - 1 ? () => setLightboxIdx(lightboxIdx + 1) : null}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Capture log view — per-ordinal status (success / failed-with-reason).
// Replaces the per-record screenshot noise; the goal is to see at a glance
// which specific records we've captured and which ones we haven't, plus
// why the failures failed.
// ─────────────────────────────────────────────────────────────────────────

function CaptureLogView({ jobId, totalExpected }: { jobId: string; totalExpected: number | null }) {
  const [data, setData] = useState<CaptureAttempt[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    function refresh() {
      getCaptureLog(jobId)
        .then((r) => { if (alive) setData(r.attempts); })
        .catch((e) => { if (alive) setError(String(e)); });
    }
    refresh();
    // Poll while open — capture is happening live.
    const t = setInterval(refresh, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [jobId]);

  if (error) return <div className="card p-5 text-[color:var(--color-rose-hi)]">{error}</div>;
  if (!data) return <div className="text-[color:var(--color-text-dim)] text-sm">Loading capture log…</div>;

  const succeeded = data.filter((a) => a.status === "success").length;
  const failed = data.filter((a) => a.status === "failed").length;
  const total = totalExpected ?? data.length;
  const missing = Math.max(0, total - succeeded - failed);

  if (data.length === 0) {
    return (
      <div className="card p-5 text-[color:var(--color-text-muted)] text-sm">
        No capture log for this lookup yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatBubble label="Captured" value={succeeded} accent="sage" />
        <StatBubble label="Failed" value={failed} accent="rose" />
        <StatBubble label="Not yet tried" value={missing} accent="ink" />
        <StatBubble label="Total expected" value={total} accent="orange" />
      </div>
      <div className="card-elevated overflow-hidden">
        <table className="w-full border-collapse text-[14px]">
          <thead className="bg-[color:var(--color-bg-card-hi)]">
            <tr>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">#</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">Status</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">Charge</th>
              <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">Tries</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">Last error</th>
            </tr>
          </thead>
          <tbody>
            {data.map((a) => {
              const ok = a.status === "success";
              return (
                <tr
                  key={a.ordinal}
                  className="border-t border-[color:var(--color-line)]"
                  style={{
                    background: ok ? "transparent" : "color-mix(in oklab, var(--color-rose-soft) 30%, transparent)",
                  }}
                >
                  <td className="px-4 py-2 tabular text-[color:var(--color-text-dim)]">{a.ordinal + 1}</td>
                  <td className="px-4 py-2">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.15em]"
                      style={{
                        color: ok ? "var(--color-sage-hi)" : "var(--color-rose-hi)",
                        background: ok ? "var(--color-sage-soft)" : "var(--color-rose-soft)",
                      }}
                    >
                      {ok ? "✓ saved" : "✗ failed"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-[color:var(--color-text)]">
                    {a.description || a.statute || (
                      <span className="italic text-[color:var(--color-text-dim)]">unknown</span>
                    )}
                    {a.statute && a.description && (
                      <span className="mono ml-2 text-[11px] text-[color:var(--color-text-dim)]">{a.statute}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 tabular text-right text-[color:var(--color-text-muted)]">{a.attempts}</td>
                  <td className="px-4 py-2 text-[12px] text-[color:var(--color-text-dim)]">
                    {ok ? "" : a.last_error || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatBubble({ label, value, accent }: { label: string; value: number; accent: "sage" | "rose" | "ink" | "orange" }) {
  const palette: Record<string, { bg: string; ink: string }> = {
    sage:   { bg: "linear-gradient(155deg, #5e7a3f 0%, #4a6131 100%)", ink: "#fff" },
    rose:   { bg: "linear-gradient(155deg, #C13455 0%, #7A1F3A 100%)", ink: "#fff" },
    ink:    { bg: "var(--color-bg-card-hi)", ink: "var(--color-text)" },
    orange: { bg: "linear-gradient(155deg, #d77b1f 0%, #8e4708 100%)", ink: "#fff" },
  };
  const p = palette[accent]!;
  return (
    <div className="rounded-2xl p-4" style={{ background: p.bg, color: p.ink }}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-85">{label}</div>
      <div className="display tabular mt-1" style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Hero — Havoc Score is the headline now. Massive number. Tagline.
// ─────────────────────────────────────────────────────────────────────────

function HavocHero({
  score,
  name,
  nickname,
  scoreBg,
  preliminary,
  jobId,
  dob,
}: {
  score: number | null;
  name: string;
  /** LLM-generated noir-style handle. Shown above the real name in italic
   *  Fraunces — small but distinctive. NULL until commentary runs. */
  nickname: string | null;
  scoreBg: string;
  /** True when one or more external sources are still in flight. The
   *  Havoc score the user sees is based on MPA only — when externals
   *  finish, we re-score on the server. */
  preliminary?: boolean;
  /** For the inline pin toggle in the upper-right corner of the hero. */
  jobId: string;
  dob: string;
}) {
  const tier = havocTier(score);
  // Pin toggle — replaces the standalone PinStarButton that used to
  // render below the hero. Same persistence (localStorage via PINNED_KEY)
  // so the Today-page tray picks it up; just inlined as an icon button
  // in the score corner so the page is one row tighter.
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    setPinned(isPinned(jobId));
    const onStorage = (e: StorageEvent) => {
      if (e.key === PINNED_KEY) setPinned(isPinned(jobId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [jobId]);
  const onTogglePin = () => {
    const nowPinned = togglePinned({ jobId, name, dob });
    setPinned(nowPinned);
  };

  return (
    <div
      className="page-hero page-hero-wave hero-cinematic"
      style={{
        background: scoreBg,
        color: "var(--color-hero-text)",
        // Override .page-hero's default top padding only. Bottom padding
        // is controlled by .page-hero-wave (which adds the 14px wave
        // height onto the visual bottom-padding). Min must clear the
        // PageHelpHandle (?) circle which sits absolutely at top:16
        // left:18 with 30px diameter — i.e. its bottom edge is at y=46.
        // Anything less and the nickname text collides with the pill.
        paddingTop: "clamp(52px, 5vw, 60px)",
      }}
    >
      <style>{`
        /* Cinematic entrance — name lands first (it's the protagonist),
           then score in the corner, then tier pill. */
        .hero-cinematic .h-name  { animation: hero-up  700ms cubic-bezier(0.16, 1, 0.3, 1) 100ms both; }
        .hero-cinematic .h-nick  { animation: hero-up  600ms cubic-bezier(0.16, 1, 0.3, 1)   0ms both; }
        .hero-cinematic .h-score { animation: hero-up  600ms cubic-bezier(0.16, 1, 0.3, 1) 500ms both; }
        .hero-cinematic .h-tier  { animation: hero-up  500ms cubic-bezier(0.16, 1, 0.3, 1) 700ms both; }
        @keyframes hero-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .hero-cinematic .h-name, .hero-cinematic .h-nick,
          .hero-cinematic .h-score, .hero-cinematic .h-tier { animation: none; }
        }
      `}</style>

      {/* Score sits in the upper-right corner — significantly smaller
          than the name so the name reads as the protagonist. */}
      <div
        className="h-score"
        style={{
          position: "absolute",
          top: "clamp(14px, 1.8vw, 20px)",
          right: "clamp(16px, 2.4vw, 28px)",
          textAlign: "right",
          zIndex: 2,
        }}
      >
        {score === null || tier === null ? (
          <div
            className="display tabular"
            style={{
              fontSize: "clamp(22px, 2.6vw, 36px)",
              lineHeight: 0.9,
              color: "var(--color-hero-text-dim, rgba(255,255,255,0.55))",
              fontVariationSettings: '"opsz" 144, "SOFT" 30',
              fontWeight: 600,
              animation: "person-havoc-pending 2.4s ease-in-out infinite",
            }}
          >
            —
          </div>
        ) : (
          <>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--color-hero-text-dim, rgba(255,255,255,0.55))",
                marginBottom: 2,
              }}
              title={HAVOC_SCORE_EXPLAINER}
            >
              Havoc
            </div>
            <div
              className="display tabular"
              style={{
                color: "var(--color-hero-text)",
                fontSize: "clamp(28px, 4.4vw, 76px)",
                lineHeight: 0.9,
                letterSpacing: "-0.025em",
                fontVariationSettings: '"opsz" 144, "SOFT" 30',
                fontWeight: 700,
              }}
              title={preliminary ? "Preliminary: based on NJ Municipal records only. Will refresh when all external sources finish." : undefined}
            >
              {score}
              {preliminary && (
                <span
                  style={{
                    fontSize: "0.45em",
                    opacity: 0.6,
                    marginLeft: 4,
                    fontWeight: 400,
                    verticalAlign: "super",
                  }}
                  aria-label="preliminary"
                >
                  *
                </span>
              )}
            </div>
            {/* Tier pill — moved back to the right corner under the
                score number. Right-aligned so the descenders in the
                name (g, y, p, j) don't collide with it. */}
            {tier && (
              <div className="h-tier mt-2" style={{ display: "flex", justifyContent: "flex-end" }}>
                <span
                  className="inline-block rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]"
                  style={{
                    color: tier.textHi,
                    borderColor: `color-mix(in oklab, ${tier.textHi} 55%, transparent)`,
                    background: `color-mix(in oklab, ${tier.textHi} 10%, transparent)`,
                  }}
                >
                  {tier.label}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pin toggle — minimalist thumbtack icon, top-right of the hero
          above the score column. Click toggles persisted-pin state.
          Replaces the old PinStarButton row below the hero. */}
      <button
        type="button"
        onClick={onTogglePin}
        aria-label={pinned ? "Unpin from Today" : "Pin to Today"}
        title={pinned ? "Unpin from Today" : "Pin to Today"}
        style={{
          position: "absolute",
          top: 14,
          right: "calc(clamp(16px, 2.4vw, 28px) + 90px)",
          width: 32,
          height: 32,
          borderRadius: 999,
          background: pinned ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.06)",
          border: pinned ? "1px solid rgba(255,255,255,0.45)" : "1px solid rgba(255,255,255,0.18)",
          color: "var(--color-hero-text, #fff)",
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          zIndex: 3,
          transition: "background 160ms ease, border-color 160ms ease, transform 160ms ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill={pinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          {/* Thumbtack: top cap + body + pin */}
          <path d="M12 17v5" />
          <path d="M9 17h6" />
          <path d="M7 9h10l-1.5 6h-7L7 9z" />
          <path d="M9 9V4h6v5" />
        </svg>
      </button>

      {/* Name — the protagonist. Massive italic Fraunces, dominates the
          hero. Score corner now only holds the eyebrow + number, so we
          only need ~60px of right padding to clear it on mobile. */}
      <div
        style={{
          paddingRight: "clamp(60px, 7vw, 110px)",
        }}
      >
        {nickname && (
          <div
            className="mb-2 h-nick"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "clamp(15px, 1.5vw, 20px)",
              color: "var(--color-hero-text-muted, rgba(255,255,255,0.78))",
              letterSpacing: "0.01em",
            }}
            title="LLM-generated handle from the data, observational, not official"
          >
            "{nickname}"
          </div>
        )}
        <h1
          className="page-hero-title h-name"
          style={{
            color: "var(--color-hero-text)",
            fontSize: "clamp(56px, 12vw, 168px)",
            lineHeight: 0.9,
            margin: 0,
          }}
        >
          {name}
        </h1>
        {/* Tier pill moved to the score corner (right side) so the
            name's descenders don't collide with it. Tagline removed —
            the tier label alone is enough; the tagline was superfluous. */}
        {preliminary && (
          <div
            className="mt-3 italic"
            style={{ color: "var(--color-hero-text-muted, rgba(255,255,255,0.7))", fontSize: 12 }}
          >
            * preliminary, re-scoring when all source lookups finish
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Commentary section — the editorial body. Three voices.
// ─────────────────────────────────────────────────────────────────────────

// One unified editorial section. Renders the {read, pattern, useful}
// triplet as a single flowing Fraunces serif read, separated by colored
// bold lead-ins ("The pattern.", "What's useful.") rather than three
// distinct cards. Drops legacy single-string commentary as a plain
// blockquote. No regenerate button — commentary is one-shot.
function CommentarySection({
  rawCommentary,
  recordCount,
  rawCareerPath,
  compactInsideHero,
}: {
  rawCommentary: string | null | undefined;
  recordCount: number;
  /** JSON-encoded {career, paragraph} from job.career_path. Renders as a
   *  small italic block at the end of the editorial — same column, same
   *  voice, distinct subheading. */
  rawCareerPath?: string | null;
  /** When true, renders without the outer station/container wrapper (so
   *  it can sit inside the hero card) AND fades after ~6 lines with a
   *  "Read more" expand toggle (NYT-style). */
  compactInsideHero?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const c = useMemo(() => parseCommentary(rawCommentary), [rawCommentary]);
  const careerPath = useMemo(() => {
    if (!rawCareerPath) return null;
    try {
      const o = JSON.parse(rawCareerPath) as {
        career?: string;
        paragraph?: string;
        alternates?: Array<{ career?: string; blurb?: string }>;
      };
      if (!o?.career || !o?.paragraph) return null;
      const alts = (o.alternates ?? [])
        .filter((a): a is { career: string; blurb: string } => !!a?.career && !!a?.blurb)
        .slice(0, 2);
      return { career: o.career, paragraph: o.paragraph, alternates: alts };
    } catch { /* keep null */ }
    return null;
  }, [rawCareerPath]);

  if (!c) {
    const inner = (
      <div className="text-center">
        <div className="eyebrow mb-2">The Read</div>
        <p className="text-[color:var(--color-text-dim)]">
          Commentary not yet generated{recordCount === 0 ? " (no records to read)." : "…"}
        </p>
      </div>
    );
    if (compactInsideHero) {
      return <div style={{ padding: "24px clamp(20px, 4vw, 48px)" }}>{inner}</div>;
    }
    return (
      <section className="container station station-alt">
        <div className="card">{inner}</div>
      </section>
    );
  }

  const proseStyle: React.CSSProperties = {
    fontFamily: "var(--font-serif)",
    // Compact mode bumps the body slightly down so the fade kicks in around
    // the same visual line-count as the lead, regardless of viewport.
    fontSize: compactInsideHero ? "clamp(18px, 1.6vw, 22px)" : "clamp(21px, 2vw, 28px)",
    lineHeight: 1.55,
    color: "var(--color-text)",
    fontVariationSettings: '"opsz" 36, "SOFT" 35',
  };

  // Compact-inside-hero mode: render a fade-truncated body with a
  // "Read more" toggle. The clipped height shows ~6 lines; clicking
  // the toggle reveals everything (read + pattern + useful + career path).
  if (compactInsideHero) {
    return (
      <div style={{ padding: "28px clamp(20px, 4vw, 48px) 24px" }}>
        <div className="eyebrow mb-3 text-[color:var(--color-rose-hi)]">The Read</div>
        <div
          style={{
            position: "relative",
            maxHeight: expanded ? "none" : "calc(1.55em * 6 * clamp(18px, 1.6vw, 22px) / 1em)",
            // Cap with a CSS-line approach: 6 lines × line-height × computed font-size.
            // We approximate with a fixed clamp that hits ~6 lines on each viewport.
            overflow: "hidden",
            transition: "max-height 320ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        >
          <p style={proseStyle}>{renderInlineMarkdown(c.read)}</p>

          {expanded && c.pattern && (
            <p className="mt-5" style={proseStyle}>
              <strong
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontWeight: 700,
                  color: "var(--color-gold-hi)",
                  marginRight: "0.4em",
                  fontVariationSettings: '"opsz" 36, "SOFT" 60',
                }}
              >
                The pattern.
              </strong>
              {renderInlineMarkdown(c.pattern)}
            </p>
          )}
          {expanded && c.useful && (
            <p className="mt-5" style={proseStyle}>
              <strong
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontWeight: 700,
                  color: "var(--color-sage-hi)",
                  marginRight: "0.4em",
                  fontVariationSettings: '"opsz" 36, "SOFT" 60',
                }}
              >
                What's useful.
              </strong>
              {renderInlineMarkdown(c.useful)}
            </p>
          )}
          {expanded && careerPath && (
            <p className="mt-5" style={proseStyle}>
              <strong
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontWeight: 700,
                  color: "var(--color-amber-hi, #d6a455)",
                  marginRight: "0.4em",
                  fontVariationSettings: '"opsz" 36, "SOFT" 60',
                }}
              >
                {careerPath.career}.
              </strong>
              {renderInlineMarkdown(careerPath.paragraph)}
            </p>
          )}

          {/* Bottom fade-out — only when collapsed. NYT-style. */}
          {!expanded && (
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "5em",
                pointerEvents: "none",
                background:
                  "linear-gradient(to bottom, transparent 0%, var(--color-bg-card) 95%)",
              }}
            />
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold tracking-wide transition"
          style={{
            color: "var(--color-accent)",
            letterSpacing: "0.04em",
          }}
        >
          {expanded ? "Show less" : "Read more"}
          <svg
            width="11"
            height="11"
            viewBox="0 0 11 11"
            fill="none"
            style={{
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 200ms ease",
            }}
          >
            <path
              d="M2 4 L5.5 7.5 L9 4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <section className="container station">
      {/* Was max-w-[72ch] mx-auto — that left big empty side-margins on
          desktop. Use the full container width (still readable because
          the prose font sizes scale with viewport via clamp). */}
      <div>
        <div className="eyebrow mb-5 text-[color:var(--color-rose-hi)]">The Read</div>

        <p style={proseStyle}>{renderInlineMarkdown(c.read)}</p>

        {c.pattern && (
          <p className="mt-6" style={proseStyle}>
            <strong
              style={{
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontWeight: 700,
                color: "var(--color-gold-hi)",
                marginRight: "0.4em",
                fontVariationSettings: '"opsz" 36, "SOFT" 60',
              }}
            >
              The pattern.
            </strong>
            {renderInlineMarkdown(c.pattern)}
          </p>
        )}

        {c.useful && (
          <p className="mt-6" style={proseStyle}>
            <strong
              style={{
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontWeight: 700,
                color: "var(--color-sage-hi)",
                marginRight: "0.4em",
                fontVariationSettings: '"opsz" 36, "SOFT" 60',
              }}
            >
              What's useful.
            </strong>
            {renderInlineMarkdown(c.useful)}
          </p>
        )}

        {careerPath && (
          <>
            <p className="mt-6" style={proseStyle}>
              <strong
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontWeight: 700,
                  color: "var(--color-amber-hi, #d6a455)",
                  marginRight: "0.4em",
                  fontVariationSettings: '"opsz" 36, "SOFT" 60',
                }}
                title="Suggested career path: derived from this person's offense pattern."
              >
                Suggested career path:&nbsp;{careerPath.career}.
              </strong>
              {careerPath.paragraph}
            </p>
            {careerPath.alternates.length > 0 && (
              <div
                className="mt-3 pl-4"
                style={{
                  borderLeft: "2px solid color-mix(in oklab, var(--color-amber-hi, #d6a455) 40%, transparent)",
                  fontFamily: "var(--font-serif)",
                  fontVariationSettings: '"opsz" 14',
                }}
              >
                <div
                  className="mb-2 italic"
                  style={{
                    fontSize: 13,
                    color: "color-mix(in oklab, var(--color-amber-hi, #d6a455) 75%, var(--color-text) 25%)",
                    letterSpacing: "0.01em",
                  }}
                >
                  Or, if that doesn't appeal:
                </div>
                <ul className="space-y-2 list-none p-0">
                  {careerPath.alternates.map((alt, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "clamp(15px, 1.3vw, 18px)",
                        lineHeight: 1.55,
                        color: "var(--color-text)",
                      }}
                    >
                      <strong
                        style={{
                          fontFamily: "var(--font-serif)",
                          fontStyle: "italic",
                          fontWeight: 700,
                          color: "var(--color-amber-hi, #d6a455)",
                          marginRight: "0.4em",
                        }}
                      >
                        {alt.career}.
                      </strong>
                      {alt.blurb}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <div className="meta mt-8 text-[color:var(--color-text-dim)]">
          For legal-aid intake colour, not legal advice
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Biography — long-form chronological narrative through the rap sheet.
// ─────────────────────────────────────────────────────────────────────────

// Biography — long-form chronological narrative through the rap sheet.
//
// One-shot generation: clicking "Read his/her/their bio" calls the LLM,
// then renders the resulting prose right under the button (replacing the
// CTA card). No regenerate. The persisted bio loads on subsequent visits.
function BioSection({
  jobId,
  firstName,
  bio,
  recordCount,
  onUpdated,
}: {
  jobId: string;
  firstName: string;
  bio: string | null | undefined;
  recordCount: number;
  onUpdated: () => void;
}) {
  const pronoun = pronounFor(firstName);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await regenerateBio(jobId);
      onUpdated();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), "danger");
    } finally {
      setBusy(false);
    }
  };

  if (recordCount === 0 && !bio) return null;

  // Empty state — invite the click. No inner card wrapper; the parent
  // page-card already provides the rounded panel chrome.
  if (!bio) {
    return (
      <section className="container station">
        <div className="text-center" style={{ padding: "24px 0" }}>
          <h3
            className="display"
            style={{
              fontSize: "clamp(22px, 2vw, 30px)",
              fontVariationSettings: '"opsz" 60, "SOFT" 60',
              color: "var(--color-text)",
              fontStyle: "italic",
            }}
          >
            Want the whole story?
          </h3>
          <p className="mx-auto mt-3 max-w-md text-sm text-[color:var(--color-text-muted)]">
            One paragraph per offense, chronological, with creative license to fill in the
            human moments between the bullet points. {recordCount}-chapter saga.
          </p>
          <button
            onClick={run}
            disabled={busy}
            className="btn btn-primary mt-5 px-6"
            style={{ paddingBlock: 12 }}
          >
            {busy ? "Writing it now…" : `Read ${pronoun} bio →`}
          </button>
          <p className="meta mt-3 text-[color:var(--color-text-dim)]">
            ~{Math.max(150, 200 + recordCount * 100)} words
          </p>
        </div>
      </section>
    );
  }

  const wordCount = bio.split(/\s+/).filter(Boolean).length;

  return (
    <section className="container station">
      {/* Was max-w-[68ch] mx-auto — using the full container width now
          to match The Read above and the Records section below. */}
      <div>
        <div className="mb-5">
          <div className="eyebrow" style={{ color: "var(--color-text-muted)" }}>Biography</div>
          <p
            className="meta mt-1"
            style={{ color: "var(--color-text-dim)", fontSize: 13 }}
          >
            {wordCount.toLocaleString()} words · told through {recordCount}{" "}
            record{recordCount === 1 ? "" : "s"}
          </p>
        </div>

        <article
          style={{
            // Long-form serif prose, sized to be genuinely readable on a
            // monitor rather than apologetically small. No nested card.
            fontFamily: "var(--font-serif)",
            fontSize: "clamp(18px, 1.4vw, 22px)",
            lineHeight: 1.7,
            fontVariationSettings: '"opsz" 24, "SOFT" 30',
            color: "var(--color-text)",
          }}
        >
          {bio.split(/\n{2,}/).map((para, i) => (
            <p
              key={i}
              className={
                i === 0
                  ? "first-letter:float-left first-letter:mr-2 first-letter:text-[3.5em] first-letter:font-bold first-letter:leading-[0.95] first-letter:text-[color:var(--color-rose-hi)]"
                  : "mt-6"
              }
              style={{ fontFamily: "var(--font-serif)" }}
            >
              {renderInlineMarkdown(para.trim())}
            </p>
          ))}
        </article>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────

export function PersonPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PersonDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>("cards");

  // Push the visited person onto the universal command palette's recent
  // list so ⌘K's empty-state surface shows "people I just looked at".
  useEffect(() => {
    if (id) recordPersonVisit(id);
  }, [id]);
  // "Open only" filter — when true, hides Disposed/Closed-to-MVC/Recalled
  // records so triage staff can focus on cases with live consequences.
  // Default off so first-time visitors see the full picture.
  const [openOnly, setOpenOnly] = useState(false);
  // Source filter — narrows Cards/Table to records from a single court
  // system. "all" means no filter. Pills only appear for sources that have
  // records, so the filter row collapses to nothing when only one source
  // is present (the common case for pure-municipal people).
  const [sourceFilter, setSourceFilter] = useState<SourceBucket | "all">("all");
  // Source-card toggle filter — orthogonal to the court-system bucket
  // filter above. Each entry is the SCRAPER key (mpa / pgpa / jocpa /
  // doc / mvc-*) of a source whose card the user clicked. Empty set =
  // no filter (default, shows everything). Non-empty = inclusive
  // intersection: an offense card is visible only if its origin
  // scraper is in the set.
  //
  // This stacks ON TOP of openOnly + sourceFilter; all three must
  // permit a record for it to render.
  const [activeSourceCardFilters, setActiveSourceCardFilters] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleSourceCardFilter = useCallback((key: string) => {
    setActiveSourceCardFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  // Source filter for the Shots view — different taxonomy (scraper origin:
  // mpa/pgpa/jocpa/doc/bop) than the records' court-system buckets, so we
  // keep it as its own state. Both feed the same dock filter chip; the
  // dock swaps which list of buckets it shows based on the active view.
  const [shotSourceFilter, setShotSourceFilter] = useState<string | "all">("all");
  // PDF download state — controls the headline button's visible state and
  // prevents double-clicks (which would fire a second 4MB render server-side
  // and confuse the user about whether the first one worked).
  const [pdfState, setPdfState] = useState<"idle" | "generating" | "error">("idle");
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Expungement-packet download state — same pattern as the PDF button
  // but a separate state so each can be in flight independently.
  const [packetState, setPacketState] = useState<"idle" | "generating" | "error">("idle");
  const [packetError, setPacketError] = useState<string | null>(null);
  // Personal-details modal state — when true, the modal is open.
  // The modal owns its own form state internally; we just track open/closed
  // here so we know whether to render it.
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  // Profile-edit modal — exposes every intake field (name/DOB/identifiers/
  // address/contact/sex) in one place. Lives separately from the
  // narrower PetitionerDetailsModal because the latter owns the
  // expungement-only fields (employer, income, narrative) that this
  // modal intentionally doesn't surface.
  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [gatheringModalOpen, setGatheringModalOpen] = useState(false);
  const [notesComposeOpen, setNotesComposeOpen] = useState(false);
  // Track NotesSection's loaded count so the wrapping card chrome can
  // self-hide on people who have no notes yet.
  const [notesCount, setNotesCount] = useState(0);
  // Snapshot of the petitioner_details JSON for ProfileEditModal's
  // initial state. Refreshed on save (via setDetailsVersion below).
  const [profileInitialDetails, setProfileInitialDetails] = useState<{
    streetAddress?: string;
    city?: string;
    state?: string;
    phone?: string;
    email?: string;
    sex?: "M" | "F" | "X";
  } | null>(null);
  // Bumped each time the user saves the details modal, so the packet
  // download button knows there's fresh data to pick up if it caches.
  const [detailsVersion, setDetailsVersion] = useState(0);
  // Quick-edit identifiers modal — opened from BackgroundChecksStatus
  // "Add now" buttons on skipped MVC rows. State is null when closed,
  // or { field } pointing to which input to focus first.
  const [identifiersModal, setIdentifiersModal] = useState<{ field: IdentifierField } | null>(null);
  // Court-letter modal state. `null` = closed. Two letter types share
  // the same modal component, distinguished by `kind`.
  const [letterModalKind, setLetterModalKind] =
    useState<"fineWaiver" | "warrant" | null>(null);
  // Resume builder modal — opens from the FloatingDock action menu.
  const [resumeBuilderOpen, setResumeBuilderOpen] = useState(false);

  // FloatingDock visibility — show as soon as the user has scrolled
  // even a small amount past the very top of the page. Previously we
  // only revealed it once the records section was in view, which meant
  // the Actions button (the most-used affordance on the page) was
  // hidden for the first ~600px of scroll. Now: hide only when truly
  // at the top (< 80px scrolled) OR scrolled past the records section
  // entirely.
  const recordsStartRef = useRef<HTMLDivElement | null>(null);
  const recordsEndRef = useRef<HTMLDivElement | null>(null);
  const [dockVisible, setDockVisible] = useState(false);
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      const scrollY = window.scrollY;
      const vh = window.innerHeight;
      const endEl = recordsEndRef.current;
      // Hide while at the very top — the page's header + hero own that
      // initial real estate. As soon as the user starts scrolling
      // (even a few lines), the dock fades in.
      const atTop = scrollY < 80;
      // Hide when the user has scrolled past the entire records list
      // (we're now in the editorial wrap-up below). If the end sentinel
      // is well above the viewport top, we're past it.
      const pastEnd = endEl ? endEl.getBoundingClientRect().top < vh * 0.4 : false;
      setDockVisible(!atTop && !pastEnd);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(check);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    check();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [data?.job?.id]);

  // — Action handlers extracted so the FloatingDock can call them too — //

  const downloadFullPdf = useCallback(async () => {
    const job = data?.job;
    if (!job || pdfState === "generating") return;
    setPdfState("generating");
    setPdfError(null);
    // Two-channel feedback: pdfState drives the in-menu spinner (so
    // users who keep the action menu open see "Generating PDF…"), and
    // runWithToast surfaces a bottom-right notification that persists
    // even if they close the menu — answers the "did anything happen?"
    // question instantly.
    const filename = `${job.last_name}_${job.first_name}_rapsheet.pdf`
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, "_");
    await runWithToast({
      label: "Generating Full PDF report",
      successDetail: `Downloaded ${filename}`,
      run: async () => {
        try {
          const res = await fetch(`/api/jobs/${job.id}/pdf`, { credentials: "include" });
          if (!res.ok) throw new Error(`Server returned ${res.status}`);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          setPdfState("idle");
        } catch (err) {
          setPdfError(err instanceof Error ? err.message : String(err));
          setPdfState("error");
          setTimeout(() => { setPdfState("idle"); setPdfError(null); }, 5000);
          throw err;
        }
      },
    });
  }, [data?.job, pdfState]);

  const downloadExpungementPacket = useCallback(async () => {
    const job = data?.job;
    if (!job || packetState === "generating") return;
    setPacketState("generating");
    setPacketError(null);
    const filename = `${job.last_name}_${job.first_name}_expungement_packet.zip`
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, "_");
    await runWithToast({
      label: "Building expungement packet",
      successDetail: `Downloaded ${filename}`,
      run: async () => {
        try {
          const res = await fetch(`/api/jobs/${job.id}/expungement-packet.zip`, { credentials: "include" });
          if (!res.ok) throw new Error(`Server returned ${res.status}`);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          setPacketState("idle");
        } catch (err) {
          setPacketError(err instanceof Error ? err.message : String(err));
          setPacketState("error");
          setTimeout(() => { setPacketState("idle"); setPacketError(null); }, 5000);
          throw err;
        }
      },
    });
  }, [data?.job, packetState]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setView("cards");
    setOpenOnly(false);
    setSourceFilter("all");
    setShotSourceFilter("all");
    setData(null);
    getPersonDetails(id)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  // Single source of truth for external_lookups data on this page.
  // Replaces the previous quartet of independent polls — see
  // useExternalLookups for the consolidation rationale. Anything that
  // needs external-lookup data (SourceStatusStrip, ExternalSourcesPanel,
  // OpenItemsSummary, or this preliminary-flag) reads from this hook.
  const { latest: externalLatest, loading: externalsLoading, enqueue: externalEnqueue } =
    useExternalLookups(id ?? "");

  // Derive the "still pending" flag for the Havoc-score-is-preliminary
  // banner. Replaces the dedicated externalsPending poll. Anything
  // queued/running/blocked counts as in-flight; expected sources that
  // haven't produced a row yet ALSO count (the worker just hasn't
  // gotten to them yet).
  const externalsPending = useMemo(() => {
    const rows = Object.values(externalLatest);
    const anyPending = rows.some(
      (r) => r && (r.status === "queued" || r.status === "running" || r.status === "blocked"),
    );
    // Sources we ALWAYS expect to fanout for every person. Excludes
    // deactivated sources ("doc" is off until residential proxy is in
    // place — see DEACTIVATED_SOURCES) so missing-doc rows don't
    // permanently flag the page as "still gathering".
    const expectedMissing = ["pgpa", "jocpa"].some(
      (s) => !(s in externalLatest),
    );
    return anyPending || expectedMissing;
  }, [externalLatest]);

  // Profile-completion gaps: when an external lookup errored solely
  // because the participant's profile is missing a required identity
  // field (DL / SSN / ZIP), the fix is a one-click profile edit, not
  // an engineering issue. Surfacing this as its own banner mode lets
  // operators differentiate "scrape genuinely broken" from "we just
  // need more info from the participant" — which were visually
  // identical before. We only flag fields that are STILL missing on
  // the job, so a stale error from before someone filled the field
  // doesn't keep nagging.
  const missingProfileFields = useMemo(() => {
    const need = new Set<"license_number" | "ssn" | "zip_code">();
    if (!data) return need;
    for (const row of Object.values(externalLatest)) {
      if (!row || row.status !== "error" || !row.error_message) continue;
      if (!/is required(?:\s*\([^)]*\))?\s*(?:but\s*)?(?:missing|missing or malformed)/i.test(row.error_message)) continue;
      const m = row.error_message.toLowerCase();
      if (/license_number|driver'?s? license|\bdl\b/.test(m) && !data.job.license_number?.trim()) {
        need.add("license_number");
      }
      if (/\bssn\b|social security/.test(m) && !data.job.ssn?.trim()) {
        need.add("ssn");
      }
      if (/\bzip\b|postal code/.test(m) && !data.job.zip_code?.trim()) {
        need.add("zip_code");
      }
    }
    return need;
  }, [externalLatest, data]);

  // Concrete list of pending background sources, with friendly names.
  // The banner near the top of the page uses this to read "Still
  // waiting on Superior Court, NJ Prison, and 2 more" instead of the
  // previous opaque "Background checks still in flight". Hook MUST stay
  // ABOVE the early-loading-return so it always runs the same hook
  // count per render (React #310).
  //
  // Labels + display order come from lib/externalSources so they stay
  // in sync with the per-source rows rendered by BackgroundChecksStatus
  // below the fold — renaming a source there auto-updates the banner here.
  const pendingSourceLabels = useMemo(() => {
    const hasLicense = !!data?.job.license_number;
    const out: string[] = [];
    for (const key of EXTERNAL_SOURCE_ORDER) {
      const row = externalLatest[key as keyof typeof externalLatest];
      // Row exists + is in-flight → pending.
      if (row && (row.status === "queued" || row.status === "running" || row.status === "blocked")) {
        out.push(sourceLabel(key));
        continue;
      }
      // Row missing entirely → pending (worker hasn't queued it yet).
      // MVC sources without a license on file never run, so don't
      // name them as "pending" (would mislead the user).
      if (!row && !(key.startsWith("mvc-") && !hasLicense)) {
        out.push(sourceLabel(key));
      }
    }
    return out;
  }, [externalLatest, data?.job.license_number]);

  // If this job gets deleted (in another tab, or just rolled off the queue),
  // bail out instead of leaving a page full of 404'd screenshots.
  useEffect(() => {
    if (!id) return;
    const es = new EventSource("/events");
    es.onmessage = (msg) => {
      try {
        const evt = JSON.parse(msg.data) as ServerEvent;
        if (evt.type === "job_deleted" && evt.jobId === id) {
          toast("That person was removed.", "warn");
          navigate("/records");
        }
      } catch (err) {
        // Don't swallow silently — log so "data isn't refreshing" is
        // debuggable from the console. We don't toast here because
        // a single malformed message shouldn't bother the user.
        // eslint-disable-next-line no-console
        console.warn("[PersonPage] failed to parse SSE message:", err, msg.data);
      }
    };
    es.onerror = () => {
      // The browser auto-reconnects on transient network blips; this
      // handler fires for both. Log once per error event so a stuck
      // connection is visible during debugging without spamming the
      // toast queue.
      // eslint-disable-next-line no-console
      console.warn("[PersonPage] /events SSE connection error");
    };
    return () => es.close();
  }, [id, navigate]);

  // Score-themed taunt when a person opens (after data loads, once per
  // navigation). Declared BEFORE any conditional early-return so React's
  // hook order stays stable across renders.
  useEffect(() => {
    if (!data) return;
    playForScore(data.job.havoc_score ?? null);
    // Key on the job id only (not the whole data object) so a background
    // refresh doesn't re-trigger the voice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.job.id]);

  // Synthesized Superior Court (PGPA) records, merged into the cards
  // view so staff don't have to context-switch between the offense-card
  // list and the External Sources panel to see indictable history.
  // Per-card phantom + char-split heals applied; see helpers up top.
  const pgpaSynthRecords = useMemo(() => {
    const pgpaRow = externalLatest.pgpa;
    const jobId = data?.job?.id ?? "";
    if (!pgpaRow?.raw_json) return [] as RecordRow[];
    try {
      const obj = JSON.parse(pgpaRow.raw_json) as { cases?: unknown };
      const cases = Array.isArray(obj.cases) ? (obj.cases as RawPgpaCase[]) : [];
      return cases
        .filter(isValidPgpaCase)
        .map((c, i) => synthesizePgpaRecord(c, pgpaRow.id, i, jobId));
    } catch {
      return [];
    }
  }, [externalLatest, data?.job?.id]);

  // Unified offense-card list: MPA records + synthesized PGPA cases.
  // Every downstream filter (openOnly, sourceFilter, source-card toggle)
  // operates on this list instead of data.records, so the merged view
  // is consistent across cards / table / counts.
  const unifiedRecords = useMemo(
    () => [...(data?.records ?? []), ...pgpaSynthRecords],
    [data?.records, pgpaSynthRecords],
  );

  // Pre-compute the OUTSTANDING subset of the MUNICIPAL records only.
  // Per product decision (2026-05-13), the Outstanding toggle hides only
  // Municipal records that have been Disposed or Transferred to another
  // court. Superior cases always show — their lifecycle has different
  // semantics ("remanded down" is still operationally meaningful).
  //
  // Warrant escape (2026-05-20, per Damon): a record with an outstanding
  // warrant ALWAYS stays visible regardless of status. Camron Herbert's
  // W 2026 1522 (Jersey City Municipal) was marked "Transferred To" but
  // the warrant was still Outstanding-Y — that's an arrestable status
  // and must surface in the Open view even though the status-only rule
  // would hide it.
  const outstandingRecords = useMemo(
    () =>
      unifiedRecords.filter((r) => {
        if (!isRecordData(r.data)) return false;
        const src = classifyRecordSource(r);
        if (src !== "municipal") return true;
        if (recordHasActiveWarrant(r.data)) return true;
        return isOpenStatus(recordStatus(r.data.summary));
      }),
    [unifiedRecords],
  );
  // Alias kept for callsites that referenced the old name.
  const openRecords = outstandingRecords;

  // Count records per source bucket. Drives the source-filter pills:
  // we only show pills for sources that actually have records, and we
  // hide the entire row if everything falls into a single bucket
  // (filtering would be a no-op).
  const sourceCounts = useMemo(() => {
    const out: Record<SourceBucket, number> = { municipal: 0, superior: 0, federal: 0, other: 0 };
    for (const r of unifiedRecords) {
      out[classifyRecordSource(r)]++;
    }
    return out;
  }, [unifiedRecords]);

  // Same idea but for SCREENSHOT sources (mpa/pgpa/jocpa/doc/bop). Drives
  // the dock filter when the active view is Shots; we only show pills for
  // sources that have at least one screenshot. Excludes captcha shots
  // since the user-facing list excludes them.
  const shotSourceCounts = useMemo(() => {
    const out = new Map<string, number>();
    for (const s of data?.screenshots ?? []) {
      if (s.kind === "captcha") continue;
      const k = s.source || "mpa";
      out.set(k, (out.get(k) ?? 0) + 1);
    }
    return out;
  }, [data?.screenshots]);
  const SHOT_SOURCE_LABEL: Record<string, string> = {
    mpa: "Municipal",
    pgpa: "Superior",
    jocpa: "Judgments",
    doc: "DOC",
  };

  // Filtered cards/table list. Two filters compose:
  //   1. openOnly  — strips Disposed/Closed-to-MVC/Recalled records.
  //   2. sourceFilter — narrows to a specific court system bucket.
  // Log + screenshots views always show everything — those are operational
  // views, not the legal-substance view that the toggle is intended for.
  // Computed BEFORE the conditional early-returns so React's hook order
  // stays stable across loading / error / loaded renders. (React #310 lurks
  // here: a useMemo that lives below an early-return runs in some renders
  // and not others, which crashes the page.)
  const visibleRecords = useMemo(() => {
    let out: RecordRow[] = openOnly ? outstandingRecords : unifiedRecords;
    if (sourceFilter !== "all") {
      out = out.filter((r) => classifyRecordSource(r) === sourceFilter);
    }
    // Source-card toggle filter — orthogonal to the court-system filter
    // above. Each active key narrows to records whose origin scraper
    // matches. Empty set = no narrowing.
    if (activeSourceCardFilters.size > 0) {
      out = out.filter((r) => activeSourceCardFilters.has(recordOriginSource(r)));
    }
    return out;
  }, [openOnly, sourceFilter, outstandingRecords, unifiedRecords, activeSourceCardFilters]);

  // NJ activity dates derived from MPA records — used by ExternalSourcesPanel
  // to infer federal time-served from the gap between latest NJ activity and
  // a BOP release date (since BOP doesn't publish admit dates). Defined
  // BEFORE the conditional early-returns so the hook order stays stable.
  const njActivityDates = useMemo(() => {
    const out: Date[] = [];
    for (const r of data?.records ?? []) {
      const rdata = r.data as { summary?: Record<string, string | string[]> } | undefined;
      const s = rdata?.summary;
      if (!s) continue;
      const candidates: string[] = [];
      const grab = (k: string) => {
        const v = s[k];
        if (typeof v === "string") candidates.push(v);
        else if (Array.isArray(v) && typeof v[0] === "string") candidates.push(v[0]);
      };
      grab("Court Date/Time"); grab("Court Date"); grab("Filing Date");
      grab("Offense Date"); grab("Starting Date"); grab("Complete Payment Due Date");
      for (const c of candidates) {
        const m = c.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (m) {
          const d = new Date(`${m[3]}-${m[1]!.padStart(2, "0")}-${m[2]!.padStart(2, "0")}T00:00:00`);
          if (!isNaN(d.getTime())) { out.push(d); break; }
        }
      }
    }
    return out;
  }, [data?.records]);

  if (loading) {
    // Skeleton that mirrors the real page hero + below-fold sections so
    // the perceived load is "the layout's already there, content is
    // arriving" rather than "nothing's here, please wait."
    return (
      <section className="container station station-flush-top">
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Hero skeleton — name + havoc + bio shape */}
          <div style={{ display: "flex", gap: 18, alignItems: "stretch" }}>
            <div className="skeleton" style={{ flex: 1, height: 180, borderRadius: 14 }} />
            <div className="skeleton" style={{ width: 180, height: 180, borderRadius: 14 }} />
          </div>
          {/* Sub-row: warrants banner / strategy memo */}
          <div className="skeleton" style={{ height: 84, borderRadius: 12 }} />
          {/* Records list — three card-shaped rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="skeleton" style={{ height: 92, borderRadius: 10 }} />
            <div className="skeleton" style={{ height: 92, borderRadius: 10 }} />
            <div className="skeleton" style={{ height: 92, borderRadius: 10 }} />
          </div>
        </div>
      </section>
    );
  }
  if (error)
    return (
      <section className="container station station-flush-top">
        <div className="card p-4 text-sm text-[color:var(--color-rose-hi)]">{error}</div>
      </section>
    );
  if (!data) return null;

  const { job, records, screenshots } = data;
  // Hero is always solid bordeaux now (the design-lab Bordeaux Rich pick).
  // Tier info still surfaces via the small tier pill below the score; the
  // havoc number itself is rendered in white for high contrast on bordeaux.
  // Old per-tier gradients (heroGradientFor) are unused.
  const heroBg = "var(--color-hero-bg)";

  // Show the source-filter pills only when ≥2 distinct buckets have records.
  // With everything in one bucket, the filter is a no-op and would just be
  // visual noise.
  const sourceBuckets = (Object.keys(sourceCounts) as SourceBucket[]).filter(
    (k) => sourceCounts[k] > 0,
  );
  const showSourceFilter = sourceBuckets.length >= 2;

  // Single profile-edit launcher used by both the inline "Edit profile"
  // button below the hero AND the "Complete profile" banner CTA. Fetches
  // the latest petitioner_details before opening so the modal hydrates
  // with whatever was last saved (possibly in another tab).
  const openProfileEditModal = async () => {
    try {
      const res = await fetch(`/api/jobs/${job.id}/petitioner-details`, { credentials: "include" });
      const detailsRes = await res.json() as { details?: typeof profileInitialDetails };
      setProfileInitialDetails(detailsRes.details ?? null);
    } catch {
      setProfileInitialDetails(null);
    }
    setProfileEditOpen(true);
  };

  // Currently-processing flag — surfaces a clear banner above the hero
  // when this job is still actively being captured so Damon knows the
  // record isn't final and more is coming.
  const isProcessing =
    job.status === "queued" ||
    job.status === "running" ||
    job.status === "waiting_captcha";

  // Friendly names for the missing-profile-fields banner.
  const FIELD_LABEL: Record<"license_number" | "ssn" | "zip_code", string> = {
    license_number: "driver's license number",
    ssn: "Social Security number",
    zip_code: "5-digit ZIP",
  };
  const missingFieldList = [...missingProfileFields].map((k) => FIELD_LABEL[k]);

  // Show the data-incomplete banner when EITHER the main MCCS scrape is
  // still running OR any background-records check is still in flight.
  // Previously fired only on MCCS — but if MCCS finished and DOC/PGPA
  // were still running, the page LOOKED final and a non-technical user
  // could miss the small "preliminary" asterisk on the score and act on
  // incomplete data. The banner is unmissable; explicit "don't trust
  // totals yet" language calls out the risk.
  const showIncompleteBanner = isProcessing || externalsPending;

  // (gatheringChip and the profile-completion chip are now rendered
  //  inline in the JSX below, as a row of pills ABOVE the hero card —
  //  not absolute-positioned overlays inside it. Avoids overlap with
  //  the nickname.)

  return (
    <div className="page-shell">
      {/* The original full-width "still gathering" banner used to sit
          here and overlap the shaded hero. It's now a compact overlay
          chip rendered inside the hero block (see gatheringChip below). */}
      {false && (
        <section className="container station station-flush-top" style={{ paddingBottom: 0 }}>
          <div
            className="card-elevated flex items-start gap-3 px-5 py-3 text-sm"
            style={{
              background: "var(--color-orange-soft)",
              border: "1px solid color-mix(in oklab, var(--color-orange-hi) 35%, transparent)",
              color: "var(--color-text)",
            }}
          >
            <span
              className="dot dot-pulse flex-shrink-0 mt-1.5"
              style={{ background: "var(--color-orange-hi)" }}
            />
            <div className="flex-1">
              <div>
                <strong style={{ color: "var(--color-orange-hi)" }}>
                  Still gathering data, don'
                </strong>
              </div>
              <div className="mt-0.5 text-[12.5px]" style={{ color: "var(--color-text-muted)" }}>
                {(() => {
                  const parts: React.ReactNode[] = [];
                  if (isProcessing) {
                    if (job.search_result_count != null) {
                      parts.push(
                        `Pulling court records (${job.record_count_captured ?? 0} of ${job.search_result_count} so far).`,
                      );
                    } else {
                      parts.push(job.step ?? "Pulling court records.");
                    }
                  }
                  if (externalsPending) {
                    // List the specific pending sources by friendly name
                    // so the user knows EXACTLY what's still loading. If
                    // the list is too long (>4) we cap it + summarize.
                    if (pendingSourceLabels.length > 0) {
                      const pretty =
                        pendingSourceLabels.length === 1
                          ? pendingSourceLabels[0]
                          : pendingSourceLabels.length <= 4
                            ? pendingSourceLabels.slice(0, -1).join(", ") +
                              (pendingSourceLabels.length > 1 ? ", and " : "") +
                              pendingSourceLabels[pendingSourceLabels.length - 1]
                            : `${pendingSourceLabels.slice(0, 3).join(", ")}, and ${pendingSourceLabels.length - 3} more`;
                      parts.push(`Still waiting on: ${pretty}.`);
                    } else {
                      parts.push("Background checks still in flight.");
                    }
                  }
                  return (
                    <>
                      {parts.join(" ")}{" "}
                      {/* Jump-link to the BackgroundChecksStatus section
                          further down the page. Smooth-scrolls so the
                          user doesn't lose their place. */}
                      <a
                        href="#background-checks-status"
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById("background-checks-status");
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }}
                        style={{
                          color: "var(--color-orange-hi)",
                          textDecoration: "underline",
                          textUnderlineOffset: 2,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        See status ↓
                      </a>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Status chips ABOVE the hero card — flat row of pills using the
          page bg as their backdrop. Moves them out of the hero's
          absolute-positioned overlay (which was overlapping the
          nickname). Renders only when there's at least one chip. */}
      {(showIncompleteBanner || missingProfileFields.size > 0) && (
        <div
          className="flex flex-wrap items-center gap-2"
          style={{ marginBottom: -8 }}
        >
          {showIncompleteBanner && (
            <button
              type="button"
              onClick={() => setGatheringModalOpen(true)}
              title="Click for full details on every source we're checking"
              className="flex items-center gap-2"
              style={{
                padding: "5px 11px 5px 9px",
                borderRadius: 999,
                background: "var(--color-bg-card-hi)",
                border: "1px solid var(--color-line-hi)",
                cursor: "pointer",
              }}
            >
              <span
                className="dot dot-pulse flex-shrink-0"
                style={{ background: "var(--color-accent)", width: 6, height: 6 }}
              />
              <span
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 0.4,
                  color: "var(--color-text-muted)",
                }}
              >
                Still gathering data
              </span>
              <span style={{ color: "var(--color-text-dim)", fontSize: 10 }}>›</span>
            </button>
          )}
          {missingProfileFields.size > 0 && (
            <button
              type="button"
              onClick={openProfileEditModal}
              title={
                missingFieldList.length === 1
                  ? `Add ${missingFieldList[0]} so MVC checks can run.`
                  : `Add ${missingFieldList.join(" + ")} so MVC checks can run.`
              }
              className="flex items-center gap-2"
              style={{
                padding: "7px 14px 7px 11px",
                borderRadius: 999,
                // Gold-tinted chip — the existing grey version was too
                // easy to miss. This is a quiet but unmistakable
                // attention-grab that says "this profile isn't done yet"
                // without screaming the way a red banner would.
                background: "color-mix(in oklab, var(--color-accent) 16%, var(--color-bg-card-hi))",
                border: "1px solid color-mix(in oklab, var(--color-accent) 55%, transparent)",
                color: "var(--color-accent)",
                cursor: "pointer",
                boxShadow: "0 0 0 3px color-mix(in oklab, var(--color-accent) 8%, transparent)",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--color-accent)",
                  flexShrink: 0,
                  boxShadow: "0 0 6px color-mix(in oklab, var(--color-accent) 60%, transparent)",
                }}
              />
              <span
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  color: "var(--color-accent)",
                }}
              >
                Add missing profile fields
                <span style={{ opacity: 0.7, marginLeft: 6, fontSize: 11, fontWeight: 500 }}>
                  · {missingFieldList.length} {missingFieldList.length === 1 ? "field" : "fields"}
                </span>
              </span>
              <span style={{ color: "var(--color-accent)", opacity: 0.75, fontSize: 11, fontWeight: 700 }}>›</span>
            </button>
          )}
        </div>
      )}

      <div className="page-card page-card-flush" data-tour="person-hero" style={{ position: "relative" }}>
        <div style={{ position: "absolute", top: 16, left: 18, zIndex: 3 }}>
          <PageHelpHandle
            query="How should I work through this person's case? What are the most important things to look at first?"
            topicId="whats-in-the-legal-assessment"
          />
        </div>
        <HavocHero
          score={job.havoc_score ?? null}
          name={`${job.first_name} ${job.last_name}`}
          nickname={job.nickname ?? null}
          scoreBg={heroBg}
          preliminary={externalsPending}
          jobId={job.id}
          dob={job.date_of_birth}
        />
        {/* Commentary lives in the SAME card as the hero — bordeaux block
            on top, "The Read" body below it on the bg-card surface. The
            compact mode shows ~6 lines then fades + Read more. */}
        <CommentarySection
          rawCommentary={job.commentary}
          recordCount={records.length}
          rawCareerPath={job.career_path}
          compactInsideHero
        />
      </div>

      {/* PinStarButton mount removed — pin toggle now lives as a
          minimalist thumbtack icon in the upper-right of the hero
          (HavocHero owns the state inline). */}

      {/* Edit-profile launcher used to live as a standalone secondary
          button row here. It's been moved into the Actions menu (under
          "For our records") + the upper-right "Complete profile" chip
          on the hero opens the same modal. Removing the standalone
          button gives the page back ~50px of vertical real estate
          right where the eye lands after the hero. */}

      {profileEditOpen && (
        <ProfileEditModal
          job={job}
          initialDetails={profileInitialDetails}
          onClose={() => setProfileEditOpen(false)}
          onSaved={(updatedJob) => {
            // Refresh the page state with the patched job + bump details
            // version so anything caching petitioner_details re-fetches.
            setData((prev) => prev ? { ...prev, job: updatedJob } : prev);
            setDetailsVersion((v) => v + 1);
          }}
        />
      )}

      {gatheringModalOpen && (
        <GatheringStatusModal
          mccsStatus={
            job.status === "queued" || job.status === "running" || job.status === "waiting_captcha"
              ? job.status
              : job.status === "completed"
                ? "completed"
                : job.status === "error"
                  ? "error"
                  : "idle"
          }
          mccsRecordsCaptured={job.record_count_captured ?? null}
          mccsExpectedTotal={job.search_result_count ?? null}
          mccsStep={job.step ?? null}
          mccsErrorMessage={job.error_message ?? null}
          externalLatest={externalLatest}
          profile={{
            license_number: job.license_number,
            ssn: job.ssn,
            zip_code: job.zip_code,
          }}
          onEditProfile={() => {
            setGatheringModalOpen(false);
            void openProfileEditModal();
          }}
          onClose={() => setGatheringModalOpen(false)}
        />
      )}

      {/* Triage summary — every "open item" for this person at a glance:
          outstanding fines (MCCS time-payment + MVC surcharge + restoration),
          active warrants, upcoming court dates and payment-due dates. Each
          line links to the deeper section/card on the same page so a
          staffer can jump to the underlying detail with one click.

          The component renders nothing when the person has no open items,
          so a fully-resolved person doesn't get an empty placeholder. The
          ErrorBoundary is belt-and-suspenders: if any unexpected record
          shape causes a render throw, the rest of the page still renders
          (no fallback content — silent degrade is fine here, the data is
          all available in the sections below). */}
      {/* Active-warrant banner — pinned at the very top of the action
          stack because warrants outrank everything else: a traffic stop
          becomes custody the second a warrant is open, so this is louder
          than license-suspension or fines. Self-hides when no record
          carries an Outstanding-Y / Active Warrant flag. Click any warrant
          row to see charge + dates + bail; per-court letter download is
          inline on each court block. */}
      <ErrorBoundary label="ActiveWarrantsBanner">
        <ActiveWarrantsBanner jobId={job.id} records={records} />
      </ErrorBoundary>

      {/* License-suspension banner — pinned ABOVE OpenItemsSummary because
          a held license is more urgent than money owed (you can pay fines
          on a payment plan; you can't earn the money to pay if you can't
          drive to work). Self-hides when no record carries the flag. */}
      <ErrorBoundary label="LicenseSuspendedBanner">
        <LicenseSuspendedBanner jobId={job.id} records={records} />
      </ErrorBoundary>

      <ErrorBoundary label="OpenItemsSummary">
        <OpenItemsSummary
          records={records}
          upcomingDates={data.upcomingDates ?? []}
          externalLatest={externalLatest}
          externalLoading={externalsLoading}
          scrapesPending={showIncompleteBanner}
          onJump={(target) => {
            // Each link variant needs different setup before the scroll
            // can land on a real element. The targets only exist in the
            // cards view (table view doesn't render ExternalSourcesPanel
            // or per-record card IDs), and MCCS fines need the open-only
            // filter ON to actually be visible at the destination.
            //
            // setView/setOpenOnly trigger a re-render → we wait one
            // animation frame before scrolling so the target element
            // is in the DOM.
            const flash = (id: string): void => {
              const el = document.getElementById(id);
              if (!el) return;
              el.scrollIntoView({ behavior: "smooth", block: "start" });
              el.classList.add("flash-jump");
              window.setTimeout(() => el.classList.remove("flash-jump"), 2200);
            };
            if (target.kind === "record") {
              setView("cards");
              requestAnimationFrame(() => flash(`record-${target.recordId}`));
            } else if (target.kind === "mvc-source") {
              setView("cards");
              requestAnimationFrame(() => flash(`source-${target.source}`));
            } else if (target.kind === "open-records") {
              setView("cards");
              setOpenOnly(true);
              requestAnimationFrame(() => flash("source-mpa"));
            }
          }}
        />
      </ErrorBoundary>

      {/* Scrapes — every NJ public-records system we hit. MPA + 7
          externals + any news matches. Defaults to expanded so the
          per-source detail is visible without an extra click. */}
      <div className="page-card">
        <h2 className="h-section" style={{ marginBottom: 20 }}>Scrapes</h2>
        <div id="background-checks-status" />
        <BackgroundChecksStatus
          mpaRecordCount={records.length}
          mpaRunning={isProcessing}
          mpaErrored={job.status === "error" || job.status === "canceled"}
          mpaErrorMessage={
            job.error_message
            ?? (job.status === "canceled" ? "This person's lookup was canceled." : null)
          }
          externalLatest={externalLatest}
          externalLoading={externalsLoading}
          personInputs={{
            licenseNumber: job.license_number ?? null,
            ssn: job.ssn ?? null,
            zipCode: job.zip_code ?? null,
          }}
          onRetry={externalEnqueue}
          onAddIdentifier={(field) => setIdentifiersModal({ field })}
          defaultExpanded
        />
        {/* News mentions — self-hides when no high/medium matches; only
            renders inside this card when relevant. */}
        <ErrorBoundary label="NewsMentionsPanel">
          <NewsMentionsPanel newsLookup={externalLatest["news"]} />
        </ErrorBoundary>
      </div>

      {/* "The Story" (Bio) section removed — was duplicative with the
          commentary read inside the hero card. BioSection no longer
          renders on PersonPage. */}

      {/* Where to from here — actionable: expungement strategy + suggested
          resources. Both about the bridge from "who is this" to "what to
          do next." Card self-hides when neither has data. */}
      {(data.expungement || (data.resources && data.resources.length > 0)) && (
        <div className="page-card">
          <h2 className="h-section" style={{ marginBottom: 20 }}>Where to From Here</h2>
          {data.expungement && (
            <ExpungementSection
              result={data.expungement}
              strategy={job.expungement_strategy ?? null}
              incompleteData={
                (job.search_result_count ?? 0) > 0 &&
                (job.record_count_captured ?? 0) < (job.search_result_count ?? 0)
              }
              records={records}
              onJumpToRecord={(recordId) => {
                setView("cards");
                requestAnimationFrame(() => {
                  const el = document.getElementById(`record-${recordId}`);
                  if (!el) return;
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  el.classList.add("flash-jump");
                  window.setTimeout(() => el.classList.remove("flash-jump"), 2200);
                });
              }}
            />
          )}
          {data.resources && data.resources.length > 0 && (
            <ResourcesSection resources={data.resources} />
          )}
        </div>
      )}

      {/* Reviewer notes — single mount. The card chrome (rounded panel +
          giant Fraunces header) only renders when there's something to
          show; otherwise NotesSection itself returns null. The mount is
          conditional on `display: contents` so the wrapper doesn't add
          extra layout when hidden. */}
      {(notesCount > 0 || notesComposeOpen) ? (
        <div className="page-card">
          <h2 className="h-section" style={{ marginBottom: 20 }}>Notes</h2>
          <NotesSection
            jobId={job.id}
            composeOpen={notesComposeOpen}
            onComposeOpen={() => setNotesComposeOpen(true)}
            onComposeClose={() => setNotesComposeOpen(false)}
            onNotesCountChange={setNotesCount}
          />
        </div>
      ) : (
        // Hidden mount keeps the notes count current so the card appears
        // instantly when the user adds the first note via the Actions menu.
        <div style={{ display: "none" }} aria-hidden>
          <NotesSection
            jobId={job.id}
            composeOpen={false}
            onComposeOpen={() => setNotesComposeOpen(true)}
            onComposeClose={() => setNotesComposeOpen(false)}
            onNotesCountChange={setNotesCount}
          />
        </div>
      )}

      {/* Records — the actual case data. View toggle + filters live in
          the FloatingDock. */}
      <section id="source-mpa" className="page-card scroll-mt-24" data-tour="person-records">
        <h2 className="h-section" style={{ marginBottom: 20 }}>Records</h2>
        {/* Sentinel #1: marks the START of the records section. The dock
            visibility hook watches this and the matching end sentinel
            below to decide when the dock should be on screen. */}
        <div ref={recordsStartRef} aria-hidden="true" />

        {/* Outer card heading already says "Records" — no inner h2 here.
            Caption stays as a subtitle below the card heading. */}
        <p
          className="h-section-caption"
          style={{ marginTop: -10, marginBottom: 16 }}
        >
          {records.length} captured ·{" "}
          <span style={{ color: "var(--color-text-dim)" }}>
            use the dock at the bottom of the screen to switch views, filter, and export
          </span>
        </p>

        {/* Petitioner-details modal — opens via the "Personal details"
            button above. On save, bumps detailsVersion so the packet
            button picks up fresh data on next download. */}
        {detailsModalOpen && (
          <PetitionerDetailsModal
            jobId={job.id}
            petitionerName={`${job.first_name} ${job.last_name}`}
            onClose={() => setDetailsModalOpen(false)}
            onSaved={() => setDetailsVersion((v) => v + 1)}
          />
        )}

        {/* Quick-edit identifiers modal — opened from the "Add DL #" /
            "Add SSN" / "Add ZIP" buttons on the BackgroundChecksStatus
            skipped rows. On save, the server re-fanouts external
            sources, so any newly-eligible scrape gets queued without
            another round-trip from the user. We refresh the lookups
            map immediately on close so the UI picks up the new state. */}
        {identifiersModal && (
          <IdentifiersModal
            jobId={job.id}
            initial={{
              licenseNumber: job.license_number ?? null,
              ssn: job.ssn ?? null,
              zipCode: job.zip_code ?? null,
            }}
            focusField={identifiersModal.field}
            onClose={() => setIdentifiersModal(null)}
            onSaved={() => {
              // Refresh the person's full state — license_number / ssn /
              // zip_code are on the job row, so getPersonDetails() returns
              // them updated. The external lookups will refresh on their
              // own via the useExternalLookups hook (the new fanout rows
              // broadcast external_updated SSE events).
              if (id) {
                getPersonDetails(id).then(setData).catch(console.error);
              }
            }}
          />
        )}

        {/* External sources (DOC, PGPA, JOCPA) — separate from the main MPA
            records list. Only shown in the cards view since that's the
            primary "what do I know about this person" surface; the table /
            log / screenshots views are operational and stay focused on MPA. */}
        {view === "cards" && (
          <ExternalSourcesPanel
            jobId={job.id}
            njActivityDates={njActivityDates}
            mccsStatus={
              job.status === "queued" || job.status === "running" || job.status === "waiting_captcha"
                ? job.status
                : job.status === "completed"
                  ? "completed"
                  : job.status === "error"
                    ? "error"
                    : null
            }
            mccsRecordsCaptured={job.record_count_captured ?? null}
            mccsExpectedTotal={job.search_result_count ?? null}
            mccsStep={job.step ?? null}
            mccsErrorMessage={job.error_message ?? null}
            mccsCompletedAt={job.completed_at ?? null}
            jobCreatedAt={job.created_at ?? null}
            latest={externalLatest}
            loading={externalsLoading}
            onEnqueue={externalEnqueue}
            profile={{
              license_number: job.license_number,
              ssn: job.ssn,
              zip_code: job.zip_code,
            }}
            onEditProfile={openProfileEditModal}
            activeSourceCardFilters={activeSourceCardFilters}
            onSourceCardToggle={toggleSourceCardFilter}
          />
        )}

        {/* Empty-state takeover — Damon's bug: when MCCS searched against
            the wrong DOB and returned 0 records, the page rendered a
            faint "No records." gray text and Damon couldn't tell if the
            search had even run. We now render an explicit "search
            complete · 0 records on file" card with the timestamp + a
            DOB confirm strip, so the user can immediately verify the
            inputs were right (and re-run if they weren't). Renders only
            for views whose normal content is the records list. */}
        {(view === "cards" || view === "table") && visibleRecords.length === 0 && (
          <RecordsEmptyState job={job} unfilteredRecordCount={records.length} />
        )}
        {view === "cards" && visibleRecords.length > 0 && (
          <CardsView records={visibleRecords} jobId={job.id} />
        )}
        {view === "visualize" && (
          <Suspense
            fallback={
              <div
                className="card p-8 text-center text-sm"
                style={{ color: "var(--color-text-dim)" }}
              >
                Loading visualization…
              </div>
            }
          >
            <VisualizeView jobId={job.id} records={records} />
          </Suspense>
        )}
        {view === "table" && visibleRecords.length > 0 && <TableView records={visibleRecords} />}
        {view === "log" && (
          <CaptureLogView jobId={job.id} totalExpected={job.search_result_count ?? null} />
        )}
        {view === "screenshots" && (
          <ScreenshotsView
            jobId={job.id}
            shots={screenshots.filter((s) => s.kind !== "captcha")}
            openOrdinals={new Set(openRecords.map((r) => r.ordinal))}
            activeSource={shotSourceFilter}
            onActiveSourceChange={setShotSourceFilter}
            openOnly={openOnly}
            onOpenOnlyChange={setOpenOnly}
          />
        )}

        {/* Sentinel #2: marks the END of the records section. The dock
            visibility hook watches this + the start sentinel above to
            decide when to show the dock. Once this sentinel scrolls
            into view, the user has reached the bottom of records and
            the dock fades out. */}
        <div ref={recordsEndRef} aria-hidden="true" style={{ height: 1 }} />
      </section>

      {/* Persistent floating dock — auto-shows when scrolled into the
          records section above, auto-hides otherwise. Houses every
          control that used to live in the in-flow toolbar: 5 view tab
          icons, a Filter chip with popover (source pills + open-only
          toggle), and an ACTIONS button with a dropdown menu of all
          5 export actions. */}
      <FloatingDock
        visible={dockVisible}
        view={view}
        onViewChange={setView}
        viewCounts={{
          cards: visibleRecords.length,
          table: visibleRecords.length,
          log: job.search_result_count ?? records.length,
          screenshots: screenshots.filter((s) => s.kind !== "captcha").length,
        }}
        filtersApplicable={view === "cards" || view === "table" || view === "screenshots"}
        showSourceFilter={
          view === "screenshots"
            ? shotSourceCounts.size >= 2
            : sourceBuckets.length >= 2
        }
        sourceBuckets={
          view === "screenshots"
            ? [
                {
                  key: "all",
                  label: "All",
                  count: Array.from(shotSourceCounts.values()).reduce((a, b) => a + b, 0),
                },
                ...Array.from(shotSourceCounts.entries()).map(([k, count]) => ({
                  key: k,
                  label: SHOT_SOURCE_LABEL[k] ?? k.toUpperCase(),
                  count,
                })),
              ]
            : [
                { key: "all", label: "All", count: records.length },
                ...sourceBuckets.map((k) => ({ key: k, label: SOURCE_LABEL[k], count: sourceCounts[k] })),
              ]
        }
        activeSourceFilter={view === "screenshots" ? shotSourceFilter : sourceFilter}
        onSourceFilterChange={(k) => {
          if (view === "screenshots") setShotSourceFilter(k);
          else setSourceFilter(k as SourceBucket | "all");
        }}
        showOpenOnly={openRecords.length > 0}
        openOnly={openOnly}
        openCount={openRecords.length}
        totalCount={records.length}
        onOpenOnlyChange={setOpenOnly}
        jobId={job.id}
        showExpungementActions={job.status === "completed" && records.length > 0}
        onIntakePacket={() => {
          // Intake packet is just a link — handled by the menu's <a href>.
        }}
        onFullPdf={downloadFullPdf}
        pdfState={pdfState}
        onExpungementPacket={downloadExpungementPacket}
        packetState={packetState}
        expungementPetitionUrl={
          job.status === "completed" && records.length > 0
            ? `/api/jobs/${job.id}/expungement-petition.pdf`
            : undefined
        }
        onPersonalDetails={() => setDetailsModalOpen(true)}
        mvcDo21Url={`/api/jobs/${job.id}/mvc-do21.pdf`}
        clientSummaryPdfUrl={`/api/jobs/${job.id}/client-summary.pdf`}
        clientSummaryPreviewUrl={`/client-summary/${job.id}`}
        screenshotPacketHref={(() => {
          // Mirror the in-Shots-view URL so the action is reachable
          // from cards/table views too. The active source filter only
          // applies when in screenshots view (since cards uses a
          // different bucket vocab); openOnly always applies.
          const params = new URLSearchParams();
          if (view === "screenshots" && shotSourceFilter !== "all") {
            params.set("source", shotSourceFilter);
          }
          if (openOnly) params.set("openOnly", "1");
          const qs = params.toString();
          return `/api/jobs/${job.id}/screenshots-packet.pdf${qs ? `?${qs}` : ""}`;
        })()}
        screenshotPacketLabel={
          openOnly
            ? "Screenshot packet (open cases only)"
            : "Screenshot packet (PDF)"
        }
        screenshotPacketSub={(() => {
          // Count what the packet ACTUALLY contains: screenshots, not
          // records. The packet's API filter is by-record-status (open
          // cases only) but it then bundles SHOTS from those cases —
          // and not every record has shots. Counting records (8/11)
          // misled users into thinking the packet would contain 8
          // images when it really had 4.
          const allShots = data?.screenshots ?? [];
          const openOrdinalsSet = new Set(openRecords.map((r) => r.ordinal));
          const openShotCount = allShots.filter((s) => {
            const src = (s as { source?: string }).source ?? "mpa";
            if (src !== "mpa") return true; // non-MPA shots always pass the open filter
            const ord = (s as { ordinal?: number | null }).ordinal ?? null;
            return ord !== null && openOrdinalsSet.has(ord);
          }).length;
          if (openOnly) {
            return `Open MPA screenshots only · ${openShotCount} of ${allShots.length}`;
          }
          return `Bundle every saved screenshot · ${allShots.length} total`;
        })()}
        onFineWaiverLetter={() => setLetterModalKind("fineWaiver")}
        onWarrantLetter={() => setLetterModalKind("warrant")}
        onResumeBuilder={() => setResumeBuilderOpen(true)}
        onAddNote={() => setNotesComposeOpen(true)}
        onEditProfile={openProfileEditModal}
      />
      {letterModalKind && (
        <LetterModal
          jobId={job.id}
          kind={letterModalKind}
          onClose={() => setLetterModalKind(null)}
        />
      )}
      <ResumeBuilderModal
        jobId={job.id}
        open={resumeBuilderOpen}
        onClose={() => setResumeBuilderOpen(false)}
      />
    </div>
  );
}
